// src/pages/PermissionRequest.jsx
// T178 Permission Request — Multi-step flow

import React, { useState, useEffect, useRef } from 'react';
import { 
    permissions, 
    permissionCategories,
    riskTypeCategories,
    getSelectedPermissions,
    getHighestApprovalLevel,
    getApprovalLevelForRiskType,
    needsL8Approval,
    riskTypeConfig,
    groupByRiskType,
    getSelectedRiskTypes,
    hasCRP,
    getApprovedPermissionsForTeam,
    getApprovedByRiskType,
    getAccessCycleEndDate
} from '../data/permissionCatalog';
import { getApprovalLink } from '../data/dropdownData';
import { getTeamConfig, validateUsersInBatches, getMyRequests, getValidationResults, listTeamConfigs, lookupManager } from '../services/api';
import { validateUsers, checkLeaderLevel } from '../services/api';
import { deptMatch } from '../services/deptMatch';
import { deptReason, rowBlocked, rowEligibleForRT, reasonsForRT, overallEligible, mergeBasePIRows, removeBlocked, unlistedDraft, canRebaseline } from '../services/eligibility';
import { normalizeAlias, normalizeAliasList, normalizeAliasListText } from '../services/alias';

// ════════════════════════════════════════
// VERTICAL STEPPER (defined outside to prevent re-mounting)
// ════════════════════════════════════════
const VerticalStepper = ({ step, useCaseType, teamLabel, isValidated }) => {
    const steps = useCaseType === 'approved'
        ? [
            { num: 1, label: 'Setup', desc: 'Team & config' },
            { num: 5, label: 'Permissions', desc: 'Select access' },
            { num: 57, label: 'Users', desc: 'Enter logins' },
            { num: 60, label: 'Validate', desc: 'Verify users' },
            { num: 70, label: 'Submit', desc: 'Review & send' }
          ]
        : [
            { num: 1, label: 'Setup', desc: 'Team & config' },
            { num: 5, label: 'Permissions', desc: 'Select access' },
            { num: 55, label: 'Use Case', desc: 'Justification' },
            { num: 56, label: 'Leaders', desc: 'Select approvers' },
            { num: 57, label: 'Users', desc: 'Enter logins' },
            { num: 60, label: 'Validate', desc: 'Verify users' },
            { num: 70, label: 'Submit', desc: 'Review & send' }
          ];
    
    // Determine current index based on step number
    const currentIdx = steps.findIndex(s => s.num >= step);
    
    // Special: if on step 6 and validation is done, advance visual state
    const getStepState = (idx) => {
        if (idx < currentIdx) return 'completed';
        if (idx === currentIdx) {
            // If on validate step and validated, show as completed and let next be current
            if (steps[idx].num === 60 && isValidated) return 'completed';
            return 'current';
        }
        // If we're on validate (idx 2) and validated, the next step (Submit, idx 3) should be current
        if (idx === currentIdx + 1 && steps[currentIdx]?.num === 60 && isValidated) return 'current';
        return 'future';
    };
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {steps.map((s, idx) => {
                const state = getStepState(idx);
                const isCompleted = state === 'completed';
                const isCurrent = state === 'current';
                
                return (
                    <div key={s.num} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '14px', fontWeight: '700', flexShrink: 0,
                                background: isCompleted ? '#22c55e' : isCurrent ? '#ff9900' : '#e9ecef',
                                color: isCompleted ? '#fff' : isCurrent ? '#000' : '#999',
                                border: isCurrent ? '3px solid #ffcc80' : '2px solid transparent',
                                boxShadow: isCurrent ? '0 0 0 4px rgba(255,153,0,0.12)' : 'none'
                            }}>
                                {isCompleted ? '✓' : idx + 1}
                            </div>
                            {idx < steps.length - 1 && (
                                <div style={{
                                    width: '2px', height: '28px',
                                    background: isCompleted ? '#22c55e' : '#e2e6ea',
                                    margin: '4px 0'
                                }} />
                            )}
                        </div>
                        <div style={{ paddingTop: '6px' }}>
                            <div style={{ fontSize: '14px', fontWeight: isCurrent ? '700' : '500', color: isCurrent ? '#111' : isCompleted ? '#333' : '#999' }}>
                                {s.label}
                            </div>
                            <div style={{ fontSize: '12px', color: isCurrent ? '#555' : '#aaa', marginTop: '1px' }}>
                                {s.desc}
                            </div>
                            {isCompleted && idx === 0 && teamLabel && (
                                <div style={{ fontSize: '11px', color: '#22c55e', marginTop: '3px', fontWeight: '500' }}>
                                    ✓ {teamLabel}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Full-width page layout with optional stepper sidebar
const PageLayout = ({ title, subtitle, children, showStepper = true, step, useCaseType, teamLabel, isValidated }) => (
    // paddingBottom clears the fixed ContactFooter so the Continue/Submit buttons aren't hidden behind it.
    <div style={{ width: '100%', padding: '0 40px 96px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Page title */}
        <div style={{ marginBottom: '16px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111', margin: '0 0 2px 0' }}>{title}</h1>
            {subtitle && <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>{subtitle}</p>}
        </div>
        {/* Two-column: stepper + content */}
        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
            {showStepper && (
                <div style={{ width: '170px', flexShrink: 0, alignSelf: 'stretch' }}>
                    <div style={{ position: 'sticky', top: '72px' }}>
                        <VerticalStepper step={step} useCaseType={useCaseType} teamLabel={teamLabel} isValidated={isValidated} />
                    </div>
                </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
                {children}
            </div>
        </div>
    </div>
);

const PermissionRequest = ({ userAlias }) => {
    // Flow state
    const [step, setStep] = useState(1);
    // 1 = Approved vs Unlisted
    // 2 = New vs Renewal
    // 3 = Alias + L3 check
    // 4 = Function/Org/Team
    // 5 = Permission selector
    // 6 = Preview + Submit (Part 2)

    const [useCaseType, setUseCaseType] = useState(null); // 'approved' or 'unlisted'
    const [requestType, setRequestType] = useState(null); // 'new' or 'renewal'
    const [priority, setPriority] = useState('Low'); // 'Low', 'Medium', 'High'
    const [requesterAlias, setRequesterAlias] = useState(userAlias || '');
    const [requesterJobLevel, setRequesterJobLevel] = useState(null);
    const [isCheckingLevel, setIsCheckingLevel] = useState(false);
    const [levelCheckError, setLevelCheckError] = useState('');
    const [levelCheckDone, setLevelCheckDone] = useState(false);

    // Step 4: Function/Org/Team
    const [functionDropdown, setFunctionDropdown] = useState([]); // multi-select array: ['Operations Team', 'Support Function']
    const [orgName, setOrgName] = useState('');
    const [teamLabel, setTeamLabel] = useState('');
    const [teamValue, setTeamValue] = useState('');
    const [teamConfig, setTeamConfig] = useState(null); // fetched from DynamoDB
    const [allTeamConfigs, setAllTeamConfigs] = useState([]); // all teams from config
    const [isLoadingConfig, setIsLoadingConfig] = useState(false);
    const [configError, setConfigError] = useState('');

    // Smart team routing — auto-detect department
    const [userDepartment, setUserDepartment] = useState(''); // from lookupL8Manager
    const [userJobFamily, setUserJobFamily] = useState(''); // from lookupL8Manager
    const [matchingTeams, setMatchingTeams] = useState([]); // teams whose orgAliases match user's dept
    const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);
    const [departmentLoaded, setDepartmentLoaded] = useState(false);
    const [bypassJustification, setBypassJustification] = useState(''); // if user switches to unlisted despite match
    const [highPriorityJustification, setHighPriorityJustification] = useState(''); // Sev-2 details for High priority

    // Function toggle helper (multi-select)
    const toggleFunction = (fn) => {
        setFunctionDropdown(prev => 
            prev.includes(fn) ? prev.filter(f => f !== fn) : [...prev, fn]
        );
    };
    const functionOptions = [
        { value: 'Operations Team', label: 'Operations' },
        { value: 'Support Function', label: 'Support' },
        { value: 'Tech Team', label: 'Tech' }
    ];

    // Step 5: Permission selector
    const [selectedPermissions, setSelectedPermissions] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [collapsedCategories, setCollapsedCategories] = useState(new Set());

    // Renewal state
    const [renewalRequests, setRenewalRequests] = useState([]);
    const [isFetchingRenewal, setIsFetchingRenewal] = useState(false);
    const [renewalError, setRenewalError] = useState('');
    const [renewalInput, setRenewalInput] = useState('');
    const [originalRequestId, setOriginalRequestId] = useState('');
    const [previousApprovalId, setPreviousApprovalId] = useState('');
    // Permission NAMES from the original request — used as a display fallback for the
    // renewal read-only permission list when a permission id isn't in the current catalog.
    const [previousSelectedPermissionNames, setPreviousSelectedPermissionNames] = useState([]);

    // TRUE when this is a renewal of a resolved original request (deep link or picked from
    // the list). In renewal mode the request-defining fields — team, function, and the
    // selected permissions — are LOCKED (changing them makes it a NEW request, not a
    // renewal). Use case justification, leader selection, the user list, and validation
    // stay editable. Mirrors the handleSubmit-local check so payload/write-back is consistent.
    const isRenewalRequest = requestType === 'renewal' && originalRequestId && originalRequestId !== 'skip';

    // Step 6: Preview + Submit (per risk type)
    const [userListByRT, setUserListByRT] = useState({}); // { LRP: 'user1,user2', CRP: 'user3' }
    const [validationByRT, setValidationByRT] = useState({}); // { LRP: {results, selectedUsers, isValidating}, ... }
    const [isValidating, setIsValidating] = useState(false);
    const [validationResults, setValidationResults] = useState(null);
    const [serverDeptBlock, setServerDeptBlock] = useState(null); // storeResults DEPARTMENT_* refusal {code, team, users}
    const [validationProgress, setValidationProgress] = useState({ progress: 0, message: '' });
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [tableFilter, setTableFilter] = useState('all'); // 'all' | 'eligible' | 'not_eligible'
    const [ineligibleNotes, setIneligibleNotes] = useState({}); // per-user INACTIVITY notes, keyed `${rt}:${login}`
    const [sharedExceptions, setSharedExceptions] = useState({}); // shared Base PI / Job Level notes, keyed `${rt}:basePI` / `${rt}:jobLevel`
    const [showFlaggedInfoModal, setShowFlaggedInfoModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState(null);

    // Use case justification (per risk type) + NEMO gate
    const [useCaseByRT, setUseCaseByRT] = useState({}); // { LRP: 'description', CRP: 'description' }

    // Show the reassurance modal once whenever a validation returns flagged (Not Found / Not Eligible) users
    useEffect(() => {
        if (validationResults && (validationResults.totalNotFound > 0 || validationResults.totalIneligible > 0)) {
            setShowFlaggedInfoModal(true);
        }
    }, [validationResults]);

    // ════════════════════════════════════════
    // FORM STATE PERSISTENCE — survives token refresh redirects
    // ════════════════════════════════════════
    const [formRestored, setFormRestored] = useState(false);

    // Restore form state on mount (if saved before a 401 redirect)
    useEffect(() => {
        try {
            // ═══════════════════════════════════════════
            // 🆕 Read URL params from email deep links
            // e.g., /request?type=renewal&originalRequestId=PAM-ABC123
            // ═══════════════════════════════════════════
            const urlParams = new URLSearchParams(window.location.search);
            const urlOriginalId = urlParams.get('originalRequestId');
            const urlType = urlParams.get('type');
            
            if (urlOriginalId) {
                console.log('[Form] Deep link detected — originalRequestId:', urlOriginalId);
                setOriginalRequestId(urlOriginalId);
                setRenewalInput(urlOriginalId);
                setRequestType('renewal');
                // Auto-load the original request data
                loadRenewalData(urlOriginalId);
                return; // Skip localStorage restore — URL params take priority
            }
            if (urlType === 'renewal') {
                setRequestType('renewal');
            }

            // Deep link: pre-select permissions (from Access Genie or other bots)
            // e.g., /request/individual?permission=base_pi,case_locker
            const urlPermissions = urlParams.get('permission');
            if (urlPermissions) {
                const permIds = urlPermissions.split(',').map(p => p.trim()).filter(Boolean);
                if (permIds.length > 0) {
                    console.log('[Form] Deep link — pre-selecting permissions:', permIds);
                    setSelectedPermissions(new Set(permIds));
                    setStep(5); // Jump to permissions step
                }
            }

            const saved = localStorage.getItem('pam_form_state');
            if (!saved) return;
            const parsed = JSON.parse(saved);
            // Only restore if saved less than 5 minutes ago
            if (Date.now() - parsed.savedAt > 5 * 60 * 1000) {
                localStorage.removeItem('pam_form_state');
                return;
            }
            const data = parsed.data;
            if (!data) { localStorage.removeItem('pam_form_state'); return; }

            // Only restore if user had meaningful progress (past step 1 or has permissions)
            if (data.step <= 1 && (!data.selectedPermissions || data.selectedPermissions.length === 0)) {
                localStorage.removeItem('pam_form_state');
                return;
            }

            console.log('[Form] Restoring saved form state (step ' + data.step + ')...');
            if (data.step) setStep(data.step);
            if (data.useCaseType) setUseCaseType(data.useCaseType);
            if (data.requestType) setRequestType(data.requestType);
            if (data.priority) setPriority(data.priority);
            if (data.functionDropdown) setFunctionDropdown(Array.isArray(data.functionDropdown) ? data.functionDropdown : [data.functionDropdown]);
            if (data.orgName) setOrgName(data.orgName);
            if (data.teamLabel) setTeamLabel(data.teamLabel);
            if (data.teamValue) setTeamValue(data.teamValue);
            if (data.selectedPermissions && data.selectedPermissions.length > 0) {
                setSelectedPermissions(new Set(data.selectedPermissions));
            }
            if (data.userListByRT) setUserListByRT(data.userListByRT);
            if (data.useCaseByRT) setUseCaseByRT(data.useCaseByRT);
            if (data.highPriorityJustification) setHighPriorityJustification(data.highPriorityJustification);
            if (data.ineligibleNotes) setIneligibleNotes(data.ineligibleNotes);
            if (data.originalRequestId) setOriginalRequestId(data.originalRequestId);

            setFormRestored(true);
            // Clear after restore
            localStorage.removeItem('pam_form_state');
            console.log('[Form] Form state restored successfully');
        } catch (e) {
            console.warn('[Form] Could not restore form state:', e);
            localStorage.removeItem('pam_form_state');
        }
    }, []);

    // ════════════════════════════════════════
    // RENEWAL HYDRATION RECOVERY
    // The deep-link mount effect fires loadRenewalData immediately, but on a COLD
    // (unauthenticated) open the auth redirect to Federate can interrupt that fetch —
    // the user comes back with requestType='renewal' + originalRequestId still in state
    // (so isRenewalRequest is true) but team/permissions never populated (team empty,
    // useCaseType null), leaving a blank locked card and a blocked Continue.
    // This effect self-heals: once authenticated, if we're in renewal mode with a real
    // originalRequestId but the pre-fill clearly didn't land (no teamLabel AND no
    // permissions) and we're not already fetching, re-run loadRenewalData.
    const renewalHydrateTried = useRef(false);
    useEffect(() => {
        if (!userAlias) return;                                   // wait until authenticated
        if (requestType !== 'renewal') return;
        if (!originalRequestId || originalRequestId === 'skip') return;
        if (isFetchingRenewal) return;
        const alreadyLoaded = !!teamLabel || selectedPermissions.size > 0 || (previousSelectedPermissionNames && previousSelectedPermissionNames.length > 0);
        if (alreadyLoaded) return;
        if (renewalHydrateTried.current) return;                 // only auto-retry once
        renewalHydrateTried.current = true;
        console.log('[Form] Renewal data not hydrated after auth — re-loading', originalRequestId);
        loadRenewalData(originalRequestId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAlias, requestType, originalRequestId, teamLabel, isFetchingRenewal]);

    // Save form state periodically — DISABLED
    // Form state is only saved when a 401 triggers a redirect (handled in auth.js)
    // This prevents stale state from persisting across normal navigation

    // Clear saved form state on successful submission
    const clearSavedFormState = () => {
        localStorage.removeItem('pam_form_state');
    };

    // Save current form state (called before auth redirects)
    const saveCurrentFormState = () => {
        try {
            const formData = {
                step,
                useCaseType,
                requestType,
                priority,
                functionDropdown,
                orgName,
                teamLabel,
                teamValue,
                selectedPermissions: Array.from(selectedPermissions),
                userListByRT,
                useCaseByRT,
                highPriorityJustification,
                ineligibleNotes,
                originalRequestId,
            };
            localStorage.setItem('pam_form_state', JSON.stringify({
                data: formData,
                savedAt: Date.now(),
                path: window.location.pathname
            }));
        } catch (e) {
            // localStorage full or not available — ignore
        }
    };

    // Register this component's save function globally so auth.js can call it before redirect
    // Also save on page unload if user has meaningful progress (handles F5 during expired session)
    useEffect(() => {
        const saveIfNeeded = () => {
            // Only save if user has progressed past step 1 with meaningful data
            if (step <= 1 && !selectedPermissions.size) return;
            saveCurrentFormState();
        };

        const saveOnUnload = () => {
            // Save on any page unload if user has progress
            // The restore logic will ignore it if it's stale (> 5 min) or if step <= 1
            if (step > 1 || selectedPermissions.size > 0) {
                saveCurrentFormState();
            }
        };

        window.__pamSaveFormState = saveIfNeeded;
        window.addEventListener('beforeunload', saveOnUnload);
        
        return () => { 
            delete window.__pamSaveFormState;
            window.removeEventListener('beforeunload', saveOnUnload);
        };
    });
    const [nemoConfirmed, setNemoConfirmed] = useState(false);

    // Legacy single user list (for backward compat with renewal pre-fill)
    const [userList, setUserList] = useState('');

    // Unlisted use case — leader lookup (per risk type)
    const [leadersByRT, setLeadersByRT] = useState({}); // { CRP: { alias, jobLevel, title }, HRP: { ... } }
    const [isLookingUpLeader, setIsLookingUpLeader] = useState(false);
    const [leaderLookupError, setLeaderLookupError] = useState('');
    const [manualLeaderRT, setManualLeaderRT] = useState({}); // { CRP: true } — which RTs are in manual mode
    const [manualLeaderInputs, setManualLeaderInputs] = useState({}); // { CRP: 'aliasXYZ' }
    // Per-RT manual leader check state: { CRP: { checking, status: 'ok'|'too_low'|'not_found'|'error', jobLevel, msg } }
    const [manualLeaderCheck, setManualLeaderCheck] = useState({});
    // Per-RT management chain captured from the auto-detect lookup, so manual mode can offer
    // a "select from your chain" dropdown + show the full chain (parity with the T321 leader UX).
    // Shape: { CRP: [{ alias, jobLevel, title, source }] } — full chain, closest→farthest.
    const [chainByRT, setChainByRT] = useState({}); // { CRP: [{alias, jobLevel, ...}] }

    // Helpers
    const selectedArray = Array.from(selectedPermissions);
    const selectedObjects = getSelectedPermissions(selectedArray);
    const highestLevel = getHighestApprovalLevel(selectedArray);
    const riskGroups = groupByRiskType(selectedArray);
    const selectedRiskTypes = getSelectedRiskTypes(selectedArray);

    // ════════════════════════════════════════
    // PASTE FROM EXCEL HELPER
    // ════════════════════════════════════════
    const handlePasteFromClipboard = async (rt) => {
        try {
            const text = await navigator.clipboard.readText();
            if (text) {
                // Parse: handles tab-separated, newline-separated, comma-separated, or mixed; bare logins
                // ('@x', 'x@amazon.com', 'Name <x@...>' -> 'x') before the format filter.
                const parsed = normalizeAliasList(text).filter(s => /^[a-z0-9._-]+$/i.test(s));
                if (parsed.length > 0) {
                    const existing = (userListByRT[rt] || '').trim();
                    const combined = existing ? `${existing}\n${parsed.join('\n')}` : parsed.join('\n');
                    setUserListByRT(prev => ({ ...prev, [rt]: combined }));
                }
            }
        } catch (e) {
            // Clipboard API not available — ignore
        }
    };

    // ════════════════════════════════════════
    // SMART TEAM ROUTING — Auto-detect department & match teams
    // ════════════════════════════════════════
    const loadDepartmentAndTeams = async (skipUseCaseSet = false) => {
        if (departmentLoaded || isDepartmentLoading) return;
        setIsDepartmentLoading(true);
        try {
            // On localhost, skip API call — use manual flow
            if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                // Fetch team configs only for local testing
                const configResult = await listTeamConfigs().catch(() => null);
                if (configResult && configResult.teams) {
                    setAllTeamConfigs(configResult.teams.filter(t => t && t.active === true));
                }
                // No department detection on localhost — fall through to manual selection
                setDepartmentLoaded(true);
                return;
            }

            // Fetch user department from lookupL8Manager
            // Use direct fetch to avoid triggering redirect on 401
            let detectedDepartment = '';
            try {
                const { lookupL8Manager } = await import('../services/api');
                const alias = requesterAlias || userAlias;
                if (alias) {
                    const result = await lookupL8Manager(alias);
                    if (result && result.userDepartment) {
                        setUserDepartment(result.userDepartment);
                        detectedDepartment = result.userDepartment;
                    }
                    if (result && result.userJobFamily) {
                        setUserJobFamily(result.userJobFamily);
                    }
                }
            } catch (lookupErr) {
                // Auth expired → re-authenticate (state is preserved) instead of silently
                // dropping to the "no Pre-Approved" screen. lookupL8Manager throws a tagged
                // isAuthError only on a real 401/403, so non-auth errors still fall through.
                if (lookupErr && lookupErr.isAuthError) {
                    console.warn('[Dept] Department lookup auth-expired — re-authenticating…');
                    const { handleAuthFailure } = await import('../services/auth');
                    setIsDepartmentLoading(false);
                    await handleAuthFailure();   // redirects to Federate; page will reload
                    return;
                }
                console.warn('[Dept] Department lookup failed (non-auth):', lookupErr.message);
                setDepartmentLoaded(true);
                setIsDepartmentLoading(false);
                return;
            }

            // Fetch all team configs
            let teams = [];
            try {
                const configResult = await listTeamConfigs();
                if (configResult && configResult.teams) {
                    setAllTeamConfigs(configResult.teams.filter(t => t && t.active === true));
                    teams = configResult.teams.filter(t => t && t.active === true);
                }
            } catch (configErr) {
                console.warn('[Dept] Team configs fetch failed:', configErr.message);
            }

            // Auto-set use case type based on department match (skip if form was restored)
            if (!skipUseCaseSet && detectedDepartment && teams.length > 0) {
                const userDept = detectedDepartment.toLowerCase().trim();
                const matched = teams.filter(team => {
                    const aliases = (team.orgAliases || []).map(a => a.toLowerCase().trim());
                    const orgNameLower = (team.orgName || '').toLowerCase().trim();
                    return aliases.includes(userDept) || orgNameLower === userDept;
                });
                if (matched.length > 0) {
                    // Don't auto-set useCaseType — let user pick from team dropdown
                    // setUseCaseType('approved');
                } else {
                    setUseCaseType('unlisted');
                }
            }
            setDepartmentLoaded(true);
        } catch (err) {
            console.error('Department detection error:', err);
            // Graceful fallback — still allow manual flow
            setDepartmentLoaded(true);
        } finally {
            setIsDepartmentLoading(false);
        }
    };

    // Compute matching teams whenever department or team configs change
    const computeMatchingTeams = () => {
        if (!userDepartment || allTeamConfigs.length === 0) return [];
        const userDept = userDepartment.toLowerCase().trim();
        return allTeamConfigs.filter(team => {
            const aliases = (team.orgAliases || []).map(a => a.toLowerCase().trim());
            const orgNameLower = (team.orgName || '').toLowerCase().trim();
            // Exact match: user's department must be in orgAliases OR equal to orgName
            return aliases.includes(userDept) || orgNameLower === userDept;
        });
    };

    // Auto-detect department on mount when user is authenticated
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (userAlias && !departmentLoaded && !isDepartmentLoading) {
            // Check if there's saved form state — if so, don't override useCaseType
            let skipUseCaseOverride = false;
            try {
                const saved = localStorage.getItem('pam_form_state');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    if (parsed.data && parsed.data.useCaseType && (Date.now() - parsed.savedAt < 5 * 60 * 1000)) {
                        skipUseCaseOverride = true;
                    }
                }
            } catch (e) { /* ignore */ }
            loadDepartmentAndTeams(skipUseCaseOverride);
        }
    }, [userAlias]);

    // ════════════════════════════════════════
    // INLINE ALIAS VALIDATION
    // ════════════════════════════════════════
    const getInvalidAliases = (userListStr) => {
        if (!userListStr || !userListStr.trim()) return [];
        const aliases = normalizeAliasList(userListStr);
        return aliases.filter(a => !/^[a-z][a-z0-9._-]{1,30}$/i.test(a));
    };

    const getRiskBadgeStyle = (riskType) => {
        const config = riskTypeConfig[riskType] || riskTypeConfig.LRP;
        return { padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', backgroundColor: config.bg, color: config.color, border: `1px solid ${config.border}`, display: 'inline-block' };
    };

    // L3 check
    const checkRequesterLevel = async () => {
        if (!requesterAlias.trim()) return;
        
        // Skip verification on localhost (no API auth available)
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            setRequesterJobLevel(5); // Assume L5 for local testing
            setLevelCheckDone(true);
            return;
        }

        // If user is authenticated via Midway, skip the API-based L3 check
        // Midway confirms identity; the approval flow on approvals.amazon.com 
        // provides the actual access control gate. L3 users rarely have portal access.
        if (userAlias) {
            setLevelCheckDone(true);
            setRequesterJobLevel(null); // Level unknown but user is authenticated
            return;
        }
        
        setIsCheckingLevel(true);
        setLevelCheckError('');
        setLevelCheckDone(false);
        try {
            // Direct fetch for L3 check — don't auto-redirect on 401
            const { getAccessToken: getToken } = await import('../services/auth');
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://b70682k0f3.execute-api.eu-north-1.amazonaws.com/prod';
            const token = getToken();
            const headers = { 'Content-Type': 'application/json' };
            if (token) headers['Authorization'] = `Bearer ${token}`;
            
            const response = await fetch(`${API_BASE_URL}/validate`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    operation: 'validateUsers',
                    userList: requesterAlias.trim(),
                    functionDropdown: 'Operations Team',
                    teamLabel: 'PoE International',
                    teamValue: 'poe_international',
                    useCase: 'level_check',
                    justification: '',
                    priority: priority,
                    requester: requesterAlias.trim()
                })
            });
            
            if (response.status === 401) {
                setLevelCheckError('Session needs refresh. Please continue — verification will be skipped.');
                setIsCheckingLevel(false);
                return;
            }
            
            const rawResult = await response.json();
            const result = typeof rawResult.body === 'string' ? JSON.parse(rawResult.body) : rawResult;
            
            // The result may be nested — handle different response formats
            const data = result?.body ? (typeof result.body === 'string' ? JSON.parse(result.body) : result.body) : result;
            const tableData = data?.tableData || data?.results?.tableData || [];
            
            if (tableData && tableData.length > 0) {
                const user = tableData[0];
                const level = parseInt(user.jobLevel);
                setRequesterJobLevel(level);
                setLevelCheckDone(true);
                if (isNaN(level) || level <= 3) {
                    setLevelCheckError('blocked');
                }
            } else {
                // Try checking if the user data is in a different field
                const totalUsers = data?.totalUsers || 0;
                if (totalUsers === 0) {
                    setLevelCheckError('User not found in system. Please check your alias.');
                } else {
                    // User found but no tableData — might be in a job
                    setLevelCheckError('Verification timed out. Please try again.');
                }
            }
        } catch (err) {
            console.error('Level check error:', err);
            setLevelCheckError('Could not verify job level: ' + err.message);
        } finally {
            setIsCheckingLevel(false);
        }    };

    // Permission toggle
    const togglePermission = (permId) => {
        setSelectedPermissions(prev => {
            const next = new Set(prev);
            if (next.has(permId)) next.delete(permId);
            else next.add(permId);
            return next;
        });
    };

    const toggleCategory = (catId) => {
        setCollapsedCategories(prev => {
            const next = new Set(prev);
            if (next.has(catId)) next.delete(catId);
            else next.add(catId);
            return next;
        });
    };

    const filterPermissions = (perms) => {
        if (!searchQuery.trim()) return perms;
        const q = searchQuery.toLowerCase();
        return perms.filter(p =>
            p.name.toLowerCase().includes(q) ||
            p.description.toLowerCase().includes(q) ||
            p.riskType.toLowerCase().includes(q)
        );
    };

    // ════════════════════════════════════════
    // RENEWAL HELPERS
    // ════════════════════════════════════════
    const fetchRenewalRequests = async () => {
        const alias = requesterAlias || userAlias;
        if (!alias) return;
        setIsFetchingRenewal(true);
        setRenewalError('');
        try {
            const result = await getMyRequests(alias);
            const requests = (result.requests || [])
                .filter(r => r.requestId && !r.requestId.startsWith('JOB-'))
                // For renewals, prioritize APPROVED requests (the ones that could be expiring)
                .sort((a, b) => {
                    // Sort by: has accessEndDate first, then by accessEndDate ascending (soonest expiry first)
                    const aExpiry = a.accessEndDate || '';
                    const bExpiry = b.accessEndDate || '';
                    if (aExpiry && !bExpiry) return -1;
                    if (!aExpiry && bExpiry) return 1;
                    if (aExpiry && bExpiry) return aExpiry.localeCompare(bExpiry);
                    // Then by createdAt descending (most recent first)
                    return (b.createdAt || '').localeCompare(a.createdAt || '');
                });
            setRenewalRequests(requests);
            if (requests.length === 0) {
                setRenewalError('No previous requests found. You can proceed as a new request or enter a Request ID manually.');
            }
        } catch (err) {
            console.error('Renewal fetch error:', err);
            if (err.message && (err.message.includes('Session expired') || err.message.includes('401'))) {
                // Trigger re-auth — save current state and redirect
                sessionStorage.setItem('pam_auth_return_url', window.location.pathname + window.location.search);
                sessionStorage.removeItem('pam_auth_session');
                window.location.href = '/';
                return;
            }
            setRenewalError('Could not fetch previous requests. You can enter a Request ID manually or click "Skip" to proceed.');
        } finally {
            setIsFetchingRenewal(false);
        }
    };

    const loadRenewalData = async (requestId) => {
        setIsFetchingRenewal(true);
        setRenewalError('');
        try {
            const data = await getValidationResults(requestId);
            // Rebaseline only your own request (the server re-checks at submit). On refusal clear any renewal
            // state a deep link or an earlier load set, and don't let the hydration effect retry.
            const refusal = canRebaseline(data, requesterAlias || userAlias);
            if (refusal) {
                setOriginalRequestId('');
                setPreviousApprovalId('');
                setRenewalInput('');
                renewalHydrateTried.current = true;
                setRenewalError(refusal);
                return;
            }
            // Pre-fill use case type so step-1 can continue (record stores 'listed'/'unlisted';
            // the form uses 'approved'/'unlisted', so map listed → approved). Also infer from
            // requestType as a fallback (e.g. 'permission_new_unlisted').
            const rawUseCaseType = (data.useCaseType || '').toLowerCase();
            const rt = (data.requestType || '').toLowerCase();
            if (rawUseCaseType === 'approved' || rawUseCaseType === 'listed') setUseCaseType('approved');
            else if (rawUseCaseType === 'unlisted') setUseCaseType('unlisted');
            else if (rt.includes('unlisted')) setUseCaseType('unlisted');
            else if (rt) setUseCaseType('approved');
            // Pre-fill team/function/org
            if (data.functionDropdown) setFunctionDropdown(Array.isArray(data.functionDropdown) ? data.functionDropdown : [data.functionDropdown]);
            if (data.orgName) setOrgName(data.orgName);
            if (data.team || data.teamLabel) {
                setTeamLabel(data.team || data.teamLabel);
            }
            if (data.teamValue) setTeamValue(data.teamValue);
            // Pre-fill user list
            if (data.userList) setUserList(normalizeAliasListText(data.userList));
            // Pre-fill permissions
            const prevPermissions = data.selectedPermissions || data.useCaseValue?.split(',') || [];
            if (prevPermissions.length > 0) {
                setSelectedPermissions(new Set(prevPermissions.filter(id => id.trim())));
            }
            // Keep the original permission NAMES for the renewal read-only display fallback.
            if (Array.isArray(data.selectedPermissionNames) && data.selectedPermissionNames.length > 0) {
                setPreviousSelectedPermissionNames(data.selectedPermissionNames);
            }
            // Store renewal reference
            setOriginalRequestId(requestId);
            setPreviousApprovalId(data.approvalId || '');
            setRenewalInput(requestId);
            // Load team config
            const label = data.team || data.teamLabel;
            if (label) {
                try {
                    const config = await getTeamConfig(label);
                    setTeamConfig(config);
                    // Listed renewal: use the team's CURRENT org/function (the server checks orgName against the config).
                    const isListedRenewal = !(rawUseCaseType === 'unlisted' || (!rawUseCaseType && rt.includes('unlisted')));
                    if (isListedRenewal && config && config.orgName) {
                        setOrgName(config.orgName);
                        if (config.functionDropdown) setFunctionDropdown([config.functionDropdown]);
                    }
                } catch (e) { /* ignore config fetch error; the record values stay and the server check decides */ }
            }
        } catch (err) {
            if (err.message && (err.message.includes('Session expired') || err.message.includes('401'))) {
                sessionStorage.setItem('pam_auth_return_url', window.location.pathname + window.location.search);
                sessionStorage.removeItem('pam_auth_session');
                window.location.href = '/';
                return;
            }
            setRenewalError('Could not load request details: ' + err.message);
        } finally {
            setIsFetchingRenewal(false);
        }
    };

    // ════════════════════════════════════════
    // STEP 1: Combined — Use Case Type + Request Type + Team
    // ════════════════════════════════════════
    if (step === 1) {
        const approvalLink = (teamConfig && teamConfig.approvalLink) || getApprovalLink(Array.isArray(functionDropdown) ? functionDropdown.join(', ') : functionDropdown, orgName, teamValue);

        // Fetch team configs when user selects use case type (not on render — avoids auth timing issues)
        const loadTeamConfigs = () => {
            if (allTeamConfigs.length === 0 && !isLoadingConfig) {
                setIsLoadingConfig(true);
                listTeamConfigs().then(result => {
                    if (result && result.teams) setAllTeamConfigs(result.teams.filter(t => t && t.active === true));
                }).catch(() => {}).finally(() => setIsLoadingConfig(false));
            }
        };

        // Smart routing: compute matching teams based on user's department
        const currentMatchingTeams = computeMatchingTeams();
        const hasTeamMatch = departmentLoaded && currentMatchingTeams.length > 0;
        const noTeamMatch = departmentLoaded && currentMatchingTeams.length === 0;

        const canContinue = useCaseType && requestType && teamValue && functionDropdown.length > 0 && orgName;

        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle="Request access to Tenant 178 permissions">

                    {/* Requester info */}
                    {userAlias && (
                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                            <span style={{ fontSize: '16px' }}>👤</span>
                            <span style={{ fontWeight: '600', color: '#166534' }}>Requester: {requesterAlias}</span>
                            <span style={{ color: '#555' }}>— Authenticated via Midway</span>
                        </div>
                    )}

                    {/* Form state restored notification */}
                    {formRestored && (
                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
                            <span><span style={{ fontSize: '15px' }}>✅</span> <strong style={{ color: '#0073bb' }}>Your progress has been restored</strong> — session was refreshed automatically.</span>
                            <button type="button" onClick={() => setFormRestored(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0073bb', fontWeight: '700', fontSize: '16px' }}>×</button>
                        </div>
                    )}

                    {/* Department auto-detect — fires automatically on mount */}
                    {isDepartmentLoading && (
                        <div style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: '#666' }}>
                            ⏳ Checking your department...
                        </div>
                    )}

                    {/* Department detected — show result */}
                    {departmentLoaded && userDepartment && (
                        <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', marginBottom: '16px', fontSize: '14px' }}>
                            <span style={{ fontWeight: '600', color: '#0073bb' }}>🏢 Department:</span> {userDepartment}
                            {userJobFamily && <span style={{ marginLeft: '12px', color: '#555' }}>| Job Family: {userJobFamily}</span>}
                        </div>
                    )}

                    {/* SMART ROUTING: Department matches → show team selector.
                        Hidden in renewal mode — the read-only locked card covers team selection. */}
                    {!isRenewalRequest && departmentLoaded && hasTeamMatch && (
                        <div className="section">
                            {/* Team selector — always shown when department has matches */}
                            <div style={{ marginBottom: '12px' }}>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>🏢 Select Your Team</div>
                                <select
                                    value={useCaseType === 'unlisted' ? '__not_listed__' : (teamValue || '__not_selected__')}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '__not_listed__') {
                                            setUseCaseType('unlisted');
                                            setTeamValue('');
                                            setTeamLabel('');
                                            setTeamConfig(null);
                                            setFunctionDropdown([]);
                                            setOrgName('');
                                        } else if (val === '__not_selected__') {
                                            setUseCaseType(null);
                                            setTeamValue('');
                                            setTeamLabel('');
                                            setTeamConfig(null);
                                        } else {
                                            const selected = currentMatchingTeams.find(t => t.teamLabel.toLowerCase().replace(/\s+/g, '_') === val);
                                            if (selected) {
                                                setUseCaseType('approved');
                                                setTeamValue(val);
                                                setTeamLabel(selected.teamLabel);
                                                setTeamConfig(selected);
                                                if (selected.functionDropdown) setFunctionDropdown([selected.functionDropdown]);
                                                if (selected.orgName) setOrgName(selected.orgName);
                                            }
                                        }
                                    }}
                                    style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
                                >
                                    <option value="__not_selected__">-- Select your team --</option>
                                    {currentMatchingTeams.map(t => (
                                        <option key={t.teamLabel} value={t.teamLabel.toLowerCase().replace(/\s+/g, '_')}>{t.teamLabel}</option>
                                    ))}
                                    <option value="__not_listed__">My team is not listed</option>
                                </select>
                            </div>

                            {/* Green banner — shown only after user selects a listed team */}
                            {useCaseType === 'approved' && teamValue && (
                                <div style={{ padding: '14px 18px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '12px', fontSize: '14px' }}>
                                    <span style={{ fontSize: '16px' }}>✅</span> <strong style={{ color: '#166534' }}>Your department has a Pre-Approved use case.</strong>
                                    <div style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>Proceeding with the approved workflow for <strong>{teamLabel}</strong>.</div>
                                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #bbf7d0', fontSize: '13px', color: '#555' }}>
                                        <span>Need to request different permissions? </span>
                                        <button type="button" onClick={() => { setUseCaseType('unlisted'); setTeamValue(''); setTeamLabel(''); setTeamConfig(null); setFunctionDropdown([]); setOrgName(''); }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0073bb', fontSize: '13px', fontWeight: '600', textDecoration: 'underline', padding: 0 }}>
                                            Submit as Unlisted Use Case
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Amber banner — shown only after user selects "My team is not listed" */}
                            {useCaseType === 'unlisted' && (
                                <div style={{ padding: '14px 18px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '12px', fontSize: '14px' }}>
                                    <span style={{ fontSize: '16px' }}>📋</span> <strong style={{ color: '#92400e' }}>Submitting as Unlisted Use Case.</strong>
                                    <div style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>You'll proceed with the unlisted workflow, which requires Leadership Approval.</div>
                                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed #fde68a', fontSize: '13px', color: '#555' }}>
                                        <span>Found your team? </span>
                                        <button type="button" onClick={() => { setUseCaseType(null); setTeamValue(''); setTeamLabel(''); setTeamConfig(null); setFunctionDropdown([]); setOrgName(''); }}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#22c55e', fontSize: '13px', fontWeight: '600', textDecoration: 'underline', padding: 0 }}>
                                            Switch back to team selection
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* SMART ROUTING: No match → Unlisted. Hidden in renewal mode. */}
                    {!isRenewalRequest && departmentLoaded && noTeamMatch && (
                        <div className="section">
                            <div style={{ padding: '14px 18px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '12px', fontSize: '14px' }}>
                                <span style={{ fontSize: '16px' }}>📋</span> <strong style={{ color: '#92400e' }}>Your department does not have a Pre-Approved use case.</strong>
                                <div style={{ fontSize: '13px', color: '#555', marginTop: '6px' }}>You'll proceed with the unlisted use case workflow, which requires Leadership Approval.</div>
                            </div>
                            {/* Secondary option — select from all teams manually */}
                            {useCaseType !== 'approved' && (
                                <div style={{ fontSize: '13px', color: '#666', marginTop: '6px' }}>
                                    <span>Know your team is pre-approved? </span>
                                    <button type="button" onClick={() => { setUseCaseType('approved'); }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0073bb', fontSize: '13px', textDecoration: 'underline', padding: 0 }}>
                                        Select manually
                                    </button>
                                </div>
                            )}
                            {useCaseType === 'approved' && (
                                <div style={{ fontSize: '13px', color: '#666', marginTop: '6px' }}>
                                    <span>✅ You are selecting a Pre-Approved team manually. </span>
                                    <button type="button" onClick={() => { setUseCaseType('unlisted'); setTeamValue(''); setTeamLabel(''); setFunctionDropdown([]); setOrgName(''); }}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b', fontSize: '13px', textDecoration: 'underline', padding: 0 }}>
                                        Switch back to Unlisted
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Manual Use Case Type — only shown if department detection failed or not available. Hidden in renewal mode. */}
                    {!isRenewalRequest && departmentLoaded && !userDepartment && (
                    <div className="section">
                        <div className="section-title">📝 Use Case Type</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                            <button type="button" onClick={() => { setUseCaseType('approved'); loadTeamConfigs(); }}
                                style={{ padding: '16px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${useCaseType === 'approved' ? '#22c55e' : '#e2e6ea'}`, background: useCaseType === 'approved' ? '#f0fdf4' : '#fff', textAlign: 'left', position: 'relative', transition: 'all 0.15s ease' }}>
                                {useCaseType === 'approved' && <span style={{ position: 'absolute', top: '10px', right: '12px', width: '22px', height: '22px', borderRadius: '50%', background: '#22c55e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                                <div style={{ fontWeight: '700', fontSize: '14px', color: useCaseType === 'approved' ? '#166534' : '#333' }}>✅ Approved Use Case</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Pre-approved by SWAT Access team. Faster processing.</div>
                            </button>
                            <button type="button" onClick={() => setUseCaseType('unlisted')}
                                style={{ padding: '16px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${useCaseType === 'unlisted' ? '#f59e0b' : '#e2e6ea'}`, background: useCaseType === 'unlisted' ? '#fffbeb' : '#fff', textAlign: 'left', position: 'relative', transition: 'all 0.15s ease' }}>
                                {useCaseType === 'unlisted' && <span style={{ position: 'absolute', top: '10px', right: '12px', width: '22px', height: '22px', borderRadius: '50%', background: '#f59e0b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                                <div style={{ fontWeight: '700', fontSize: '14px', color: useCaseType === 'unlisted' ? '#92400e' : '#333' }}>📋 Unlisted Use Case</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Use case not yet approved. Requires Leadership Approval.</div>
                            </button>
                        </div>
                    </div>
                    )}

                    {/* Unlisted Use Case Flow */}
                    {useCaseType === 'unlisted' && (<>
                    <div className="section">
                        <div className="section-title">📋 Request Type</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                            <button type="button" onClick={() => { setRequestType('new'); setOriginalRequestId(''); }}
                                style={{ padding: '16px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${requestType === 'new' ? '#ff9900' : '#e2e6ea'}`, background: requestType === 'new' ? '#fffbf0' : '#fff', textAlign: 'left', position: 'relative', transition: 'all 0.15s ease' }}>
                                {requestType === 'new' && <span style={{ position: 'absolute', top: '10px', right: '12px', width: '22px', height: '22px', borderRadius: '50%', background: '#ff9900', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                                <div style={{ fontWeight: '700', fontSize: '14px', color: requestType === 'new' ? '#92400e' : '#333' }}>🆕 New Request</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Request new permissions for your team</div>
                            </button>
                            <button type="button" onClick={() => { setRequestType('renewal'); fetchRenewalRequests(); }}
                                style={{ padding: '16px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${requestType === 'renewal' ? '#0073bb' : '#e2e6ea'}`, background: requestType === 'renewal' ? '#f0f8ff' : '#fff', textAlign: 'left', position: 'relative', transition: 'all 0.15s ease' }}>
                                {requestType === 'renewal' && <span style={{ position: 'absolute', top: '10px', right: '12px', width: '22px', height: '22px', borderRadius: '50%', background: '#0073bb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                                <div style={{ fontWeight: '700', fontSize: '14px', color: '#333' }}>🔄 Renewal Request</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Renew existing permissions</div>
                            </button>
                        </div>
                    </div>

                    {/* Renewal lookup for unlisted */}
                    {requestType === 'renewal' && !originalRequestId && (
                        <div className="section" style={{ borderLeft: '4px solid #0073bb', paddingLeft: '16px' }}>
                            <div className="section-title" style={{ color: '#0073bb' }}>🔄 Load Previous Request</div>
                            {isFetchingRenewal && <div style={{ padding: '12px', fontSize: '13px', color: '#0073bb' }}>⏳ Loading...</div>}
                            {renewalError && <div style={{ padding: '10px 14px', borderRadius: '6px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e', marginBottom: '12px' }}>⚠️ {renewalError}</div>}
                            {!isFetchingRenewal && renewalRequests.length > 0 && (
                                <div style={{ maxHeight: '220px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e2e6ea', marginBottom: '12px' }}>
                                    {renewalRequests.slice(0, 10).map((req, idx) => {
                                        const expiryDate = req.accessEndDate;
                                        let expiryBadge = null;
                                        if (expiryDate) {
                                            const daysLeft = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
                                            const color = daysLeft <= 7 ? '#dc2626' : daysLeft <= 15 ? '#f59e0b' : '#3b82f6';
                                            const label = daysLeft < 0 ? 'Expired' : `${daysLeft}d left`;
                                            expiryBadge = <span style={{ marginLeft: 'auto', padding: '2px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', color, background: `${color}11` }}>{label}</span>;
                                        }
                                        const hasRenewal = !!req.renewalRequestId;
                                        return (
                                        <div key={req.requestId} onClick={() => !hasRenewal && loadRenewalData(req.requestId)}
                                            style={{ padding: '10px 14px', cursor: hasRenewal ? 'not-allowed' : 'pointer', borderBottom: '1px solid #f0f0f0', background: idx % 2 === 0 ? '#fff' : '#f8f9fa', opacity: hasRenewal ? 0.6 : 1 }}
                                            onMouseOver={(e) => { if (!hasRenewal) e.currentTarget.style.background = '#eef6ff'; }}
                                            onMouseOut={(e) => { if (!hasRenewal) e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8f9fa'; }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>{req.teamLabel || req.functionDropdown || 'Unknown'} — {req.useCase || 'N/A'}</div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        {req.requestId} · {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : ''}
                                                        {expiryDate && <span style={{ color: '#888' }}> · Expires: {expiryDate}</span>}
                                                    </div>
                                                    {hasRenewal && <div style={{ fontSize: '11px', color: '#22c55e', marginTop: '2px' }}>✅ Renewal submitted</div>}
                                                </div>
                                                {expiryBadge && !hasRenewal && expiryBadge}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input type="text" value={renewalInput} onChange={(e) => setRenewalInput(e.target.value)} placeholder="Or enter Request ID..." style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                                <button type="button" onClick={() => { if (renewalInput.trim()) loadRenewalData(renewalInput.trim()); }} disabled={!renewalInput.trim()} style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none', background: '#0073bb', color: '#fff' }}>Load</button>
                            </div>
                            <button type="button" onClick={() => setOriginalRequestId('skip')} style={{ marginTop: '10px', fontSize: '12px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Skip — fill manually</button>
                        </div>
                    )}

                    {/* Renewal loaded banner for unlisted */}
                    {requestType === 'renewal' && originalRequestId && originalRequestId !== 'skip' && (
                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', marginBottom: '8px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span><strong style={{ color: '#0073bb' }}>🔄 Renewing:</strong> {originalRequestId}</span>
                            <button type="button" onClick={() => { setOriginalRequestId(''); setRenewalInput(''); }} style={{ fontSize: '12px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                        </div>
                    )}

                    {/* Function / Org / Team for unlisted (manual entry) */}
                    {(requestType === 'new' || originalRequestId) && (
                        <div className="section">
                            <div className="section-title">🏢 Team Details</div>
                            {isRenewalRequest && (
                                <div style={{ marginBottom: '10px', padding: '8px 12px', borderRadius: '6px', background: '#eef6ff', border: '1px solid #b8d4f0', fontSize: '12px', color: '#0c5460' }}>
                                    🔄 Renewal — team, function and permissions are locked to match the original request. To change them, submit a new request instead.
                                </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '8px' }}>
                                <div className="form-group">
                                    <label>Function <span className="required">*</span></label>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                        {functionOptions.map(opt => (
                                            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', border: `1px solid ${functionDropdown.includes(opt.value) ? '#ff9900' : '#ddd'}`, background: functionDropdown.includes(opt.value) ? '#fffbf0' : '#fff', cursor: isRenewalRequest ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: isRenewalRequest ? 0.7 : 1 }}>
                                                <input type="checkbox" checked={functionDropdown.includes(opt.value)} disabled={isRenewalRequest} onChange={() => toggleFunction(opt.value)} style={{ width: '14px', height: '14px' }} />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Org <span className="required">*</span> <span style={{ fontSize: '12px', color: '#555', fontWeight: '400', marginLeft: '6px' }}>(Same as Department Name in <a href={`https://permissions.amazon.com/a/user/${requesterAlias || userAlias || ''}`} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb' }}>permissions.amazon.com</a>)</span></label>
                                    <input type="text" value={orgName} readOnly={isRenewalRequest} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g., TSE International"
                                        style={isRenewalRequest ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined} />
                                </div>
                                <div className="form-group">
                                    <label>Team <span className="required">*</span></label>
                                    <input type="text" value={teamLabel} readOnly={isRenewalRequest} onChange={(e) => { setTeamLabel(e.target.value); setTeamValue(e.target.value.toLowerCase().replace(/\s+/g, '_')); }} placeholder="e.g., POE International"
                                        style={isRenewalRequest ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Priority for unlisted */}
                    {useCaseType === 'unlisted' && requestType && (
                        <div className="section">
                            <div className="section-title">🚦 Priority</div>
                            <div className="form-group">
                                <select value={priority} onChange={(e) => { setPriority(e.target.value); if (e.target.value !== 'High') setHighPriorityJustification(''); }}
                                    style={{ maxWidth: '240px' }}>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </select>
                            </div>
                            {/* Sev-2 gate for High Priority */}
                            {priority === 'High' && (
                                <div style={{ marginTop: '12px', padding: '14px 16px', borderRadius: '8px', background: '#ffeef0', border: '1px solid #ffc1c8' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#d13212', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>⚠️</span> High Priority is reserved for active Sev-2 incidents only.
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
                                        Misuse of High Priority may result in request rejection. Please provide SIM/TT ticket details and describe the impact.
                                    </div>
                                    <textarea
                                        value={highPriorityJustification}
                                        onChange={(e) => setHighPriorityJustification(e.target.value)}
                                        placeholder="Provide Sev-2 details: SIM/TT ticket ID, impact description, urgency for expedited access..."
                                        style={{ width: '100%', minHeight: '80px', padding: '10px 12px', borderRadius: '6px', border: '1px solid #fca5a5', fontSize: '13px', resize: 'vertical', background: '#fff' }}
                                    />
                                    {highPriorityJustification.trim().length > 0 && highPriorityJustification.trim().length < 20 && (
                                        <div style={{ fontSize: '11px', color: '#d13212', marginTop: '4px' }}>Please provide at least 20 characters describing the Sev-2 incident.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Continue button for unlisted */}
                    {useCaseType === 'unlisted' && requestType && functionDropdown.length > 0 && orgName && teamLabel && (priority !== 'High' || highPriorityJustification.trim().length >= 20) && (
                        <button type="button" onClick={() => setStep(5)}
                            style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000', marginTop: '16px' }}>
                            Continue to Permissions →
                        </button>
                    )}
                    </>)}

                    {useCaseType === 'approved' && (<>
                    <div className="section">
                        <div className="section-title">📋 Request Type</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '8px' }}>
                            <button type="button" onClick={() => { setRequestType('new'); setOriginalRequestId(''); loadTeamConfigs(); }}
                                style={{ padding: '16px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${requestType === 'new' ? '#ff9900' : '#e2e6ea'}`, background: requestType === 'new' ? '#fffbf0' : '#fff', textAlign: 'left', position: 'relative', transition: 'all 0.15s ease' }}>
                                {requestType === 'new' && <span style={{ position: 'absolute', top: '10px', right: '12px', width: '22px', height: '22px', borderRadius: '50%', background: '#ff9900', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                                <div style={{ fontWeight: '700', fontSize: '14px', color: requestType === 'new' ? '#92400e' : '#333' }}>🆕 New Request</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Request new permissions for your team</div>
                            </button>
                            <button type="button" onClick={() => { setRequestType('renewal'); fetchRenewalRequests(); }}
                                style={{ padding: '16px', borderRadius: '10px', cursor: 'pointer', border: `2px solid ${requestType === 'renewal' ? '#0073bb' : '#e2e6ea'}`, background: requestType === 'renewal' ? '#f0f8ff' : '#fff', textAlign: 'left', position: 'relative', transition: 'all 0.15s ease' }}>
                                {requestType === 'renewal' && <span style={{ position: 'absolute', top: '10px', right: '12px', width: '22px', height: '22px', borderRadius: '50%', background: '#0073bb', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                                <div style={{ fontWeight: '700', fontSize: '14px', color: requestType === 'renewal' ? '#0073bb' : '#333' }}>🔄 Rebaseline</div>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Rebaseline existing permissions for your team</div>
                            </button>
                        </div>
                    </div>

                    {/* Renewal lookup — only when renewal selected */}
                    {requestType === 'renewal' && !originalRequestId && (
                        <div className="section" style={{ borderLeft: '4px solid #0073bb', paddingLeft: '16px' }}>
                            <div className="section-title" style={{ color: '#0073bb' }}>🔄 Load Previous Request</div>
                            {isFetchingRenewal && <div style={{ padding: '12px', fontSize: '13px', color: '#0073bb' }}>⏳ Loading...</div>}
                            {renewalError && <div style={{ padding: '10px 14px', borderRadius: '6px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e', marginBottom: '12px' }}>⚠️ {renewalError}</div>}
                            {!isFetchingRenewal && renewalRequests.length > 0 && (
                                <div style={{ maxHeight: '220px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e2e6ea', marginBottom: '12px' }}>
                                    {renewalRequests.slice(0, 10).map((req, idx) => {
                                        const expiryDate = req.accessEndDate;
                                        let expiryBadge = null;
                                        if (expiryDate) {
                                            const daysLeft = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
                                            const color = daysLeft <= 7 ? '#dc2626' : daysLeft <= 15 ? '#f59e0b' : '#3b82f6';
                                            const label = daysLeft < 0 ? 'Expired' : `${daysLeft}d left`;
                                            expiryBadge = <span style={{ marginLeft: 'auto', padding: '2px 6px', borderRadius: '8px', fontSize: '11px', fontWeight: '600', color, background: `${color}11` }}>{label}</span>;
                                        }
                                        const hasRenewal = !!req.renewalRequestId;
                                        return (
                                        <div key={req.requestId} onClick={() => !hasRenewal && loadRenewalData(req.requestId)}
                                            style={{ padding: '10px 14px', cursor: hasRenewal ? 'not-allowed' : 'pointer', borderBottom: '1px solid #f0f0f0', background: idx % 2 === 0 ? '#fff' : '#f8f9fa', opacity: hasRenewal ? 0.6 : 1 }}
                                            onMouseOver={(e) => { if (!hasRenewal) e.currentTarget.style.background = '#eef6ff'; }}
                                            onMouseOut={(e) => { if (!hasRenewal) e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8f9fa'; }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>{req.teamLabel || req.functionDropdown || 'Unknown'} — {req.useCase || 'N/A'}</div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                        {req.requestId} · {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : ''}
                                                        {expiryDate && <span style={{ color: '#888' }}> · Expires: {expiryDate}</span>}
                                                    </div>
                                                    {hasRenewal && <div style={{ fontSize: '11px', color: '#22c55e', marginTop: '2px' }}>✅ Renewal submitted</div>}
                                                </div>
                                                {expiryBadge && !hasRenewal && expiryBadge}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input type="text" value={renewalInput} onChange={(e) => setRenewalInput(e.target.value)} placeholder="Or enter Request ID..." style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                                <button type="button" onClick={() => { if (renewalInput.trim()) loadRenewalData(renewalInput.trim()); }} disabled={!renewalInput.trim()} style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none', background: '#0073bb', color: '#fff' }}>Load</button>
                            </div>
                            <button type="button" onClick={() => setOriginalRequestId('skip')} style={{ marginTop: '10px', fontSize: '12px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Skip — fill manually</button>
                        </div>
                    )}

                    {/* Renewal loaded banner */}
                    {requestType === 'renewal' && originalRequestId && originalRequestId !== 'skip' && (
                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', marginBottom: '8px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span><strong style={{ color: '#0073bb' }}>🔄 Renewing:</strong> {originalRequestId}</span>
                            <button type="button" onClick={() => { setOriginalRequestId(''); setRenewalInput(''); }} style={{ fontSize: '12px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                        </div>
                    )}

                    {/* RENEWAL: team/function/org are locked to the original — show a read-only
                        summary card instead of the selector (a disabled <select> can't reliably
                        match its option values, which left the field blank + stuck). */}
                    {isRenewalRequest && (
                        <div className="section">
                            <div className="section-title">🏢 Team & Details (locked)</div>
                            <div style={{ marginBottom: '10px', padding: '8px 12px', borderRadius: '6px', background: '#eef6ff', border: '1px solid #b8d4f0', fontSize: '12px', color: '#0c5460' }}>
                                🔄 Renewal — team, function, org and permissions are locked to match the original request ({originalRequestId}). To change them, submit a new request instead.
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div><div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Team</div><div style={{ fontWeight: '600', color: '#333' }}>{teamLabel || '—'}</div></div>
                                <div><div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Function</div><div style={{ fontWeight: '600', color: '#333' }}>{(Array.isArray(functionDropdown) ? functionDropdown.join(', ') : functionDropdown) || '—'}</div></div>
                                <div><div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Org</div><div style={{ fontWeight: '600', color: '#333' }}>{orgName || '—'}</div></div>
                            </div>
                        </div>
                    )}

                    {/* Team Selection — NEW requests only. Hidden when team already selected AND function/org are already filled */}
                    {!isRenewalRequest && (requestType === 'new' || originalRequestId) && !(hasTeamMatch && teamValue && functionDropdown.length > 0 && orgName) && (
                        <div className="section">
                            <div className="section-title">🏢 Select Your Team</div>
                            <div className="form-group">
                                <label>Team <span className="required">*</span></label>
                                <select value={teamValue} onChange={(e) => {
                                    const opt = e.target.options[e.target.selectedIndex];
                                    const selectedConfig = allTeamConfigs.find(c => c.teamLabel === opt.dataset.label);
                                    setTeamLabel(opt.dataset.label || '');
                                    setTeamValue(e.target.value);
                                    setFunctionDropdown(selectedConfig?.functionDropdown ? [selectedConfig.functionDropdown] : []);
                                    setOrgName(selectedConfig?.orgName || '');
                                }}>
                                    <option value="">-- Select Team --</option>
                                    {(hasTeamMatch ? currentMatchingTeams : allTeamConfigs).map(c => (
                                        <option key={c.teamLabel} value={c.teamLabel.toLowerCase().replace(/\s+/g, '_')} data-label={c.teamLabel}>{c.teamLabel}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Function & Org */}
                            {teamValue && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <div className="form-group">
                                        <label>Function <span className="required">*</span></label>
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                            {functionOptions.map(opt => (
                                                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', border: `1px solid ${functionDropdown.includes(opt.value) ? '#ff9900' : '#ddd'}`, background: functionDropdown.includes(opt.value) ? '#fffbf0' : '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                                                    <input type="checkbox" checked={functionDropdown.includes(opt.value)} onChange={() => toggleFunction(opt.value)} style={{ width: '14px', height: '14px' }} />
                                                    {opt.label}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Org Name <span className="required">*</span></label>
                                        <input type="text" value={orgName} readOnly
                                            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                            placeholder="Auto-filled from team config" />
                                    </div>
                                </div>
                            )}

                            {/* Valid Departments */}
                            {teamValue && (() => {
                                const selectedConfig = allTeamConfigs.find(c => c.teamLabel.toLowerCase().replace(/\s+/g, '_') === teamValue);
                                const depts = selectedConfig?.orgAliases || [];
                                return depts.length > 0 ? (
                                    <div style={{ padding: '8px 12px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', marginTop: '8px', fontSize: '12px', color: '#555' }}>
                                        <span style={{ fontWeight: '600' }}>Valid Departments:</span> {depts.join(', ')}
                                    </div>
                                ) : null;
                            })()}

                            {/* Approval link */}
                            {teamValue && approvalLink && (
                                <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', marginTop: '8px', fontSize: '13px' }}>
                                    ✅ Pre-approved. <a href={approvalLink} target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb', fontWeight: '600' }}>View Approval ↗</a>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Priority */}
                    {useCaseType && requestType && (
                        <div className="section">
                            <div className="section-title">🚦 Priority</div>
                            <div className="form-group">
                                <select value={priority} onChange={(e) => { setPriority(e.target.value); if (e.target.value !== 'High') setHighPriorityJustification(''); }}
                                    style={{ maxWidth: '240px' }}>
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="High">High</option>
                                </select>
                            </div>
                            {/* Sev-2 gate for High Priority */}
                            {priority === 'High' && (
                                <div style={{ marginTop: '12px', padding: '14px 16px', borderRadius: '8px', background: '#ffeef0', border: '1px solid #ffc1c8' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#d13212', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>⚠️</span> High Priority is reserved for active Sev-2 incidents only.
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#555', marginBottom: '10px' }}>
                                        Misuse of High Priority may result in request rejection. Please provide SIM/TT ticket details and describe the impact.
                                    </div>
                                    <textarea
                                        value={highPriorityJustification}
                                        onChange={(e) => setHighPriorityJustification(e.target.value)}
                                        placeholder="Provide Sev-2 details: SIM/TT ticket ID, impact description, urgency for expedited access..."
                                        style={{ width: '100%', minHeight: '80px', padding: '10px 12px', borderRadius: '6px', border: '1px solid #fca5a5', fontSize: '13px', resize: 'vertical', background: '#fff' }}
                                    />
                                    {highPriorityJustification.trim().length > 0 && highPriorityJustification.trim().length < 20 && (
                                        <div style={{ fontSize: '11px', color: '#d13212', marginTop: '4px' }}>Please provide at least 20 characters describing the Sev-2 incident.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Continue button */}
                    {canContinue && (priority !== 'High' || highPriorityJustification.trim().length >= 20) && (
                        <button type="button" onClick={async () => {
                            setIsLoadingConfig(true);
                            try {
                                if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
                                    const config = await getTeamConfig(teamLabel);
                                    setTeamConfig(config);
                                } else {
                                    setTeamConfig({ found: true, permissions: [], approvalLink: '' });
                                }
                                setStep(5);
                            } catch (err) {
                                setConfigError('Could not load team config: ' + err.message);
                            } finally {
                                setIsLoadingConfig(false);
                            }
                        }}
                            style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000', marginTop: '16px' }}>
                            Continue to Permissions →
                        </button>
                    )}
                    </>)}

                    {isLoadingConfig && useCaseType === 'approved' && <div style={{ marginTop: '12px', fontSize: '13px', color: '#666', textAlign: 'center' }}>⏳ Loading team permissions...</div>}
                    {configError && useCaseType === 'approved' && <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e' }}>⚠️ {configError}</div>}
            </PageLayout>
        );
    }

    // ════════════════════════════════════════
    // STEP 2: New vs Renewal
    // ════════════════════════════════════════
    if (step === 2) {
        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle={`${useCaseType === 'approved' ? 'Approved' : 'Unlisted'} Use Case`}>
                    <button type="button" onClick={() => { setStep(1); setUseCaseType(null); }}
                        style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#666', marginBottom: '16px' }}>
                        ← Back
                    </button>
                    <div className="section">
                        <div className="section-title">📋 Request Type</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                            <button type="button"
                                onClick={() => { setRequestType('new'); setStep(3); }}
                                style={{ padding: '24px 20px', borderRadius: '12px', cursor: 'pointer', border: '2px solid #e2e6ea', background: '#fff', textAlign: 'left', transition: 'all 0.2s' }}
                                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#ff9900'; e.currentTarget.style.background = '#fffbf0'; }}
                                onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e6ea'; e.currentTarget.style.background = '#fff'; }}
                            >
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>🆕</div>
                                <div style={{ fontWeight: '700', fontSize: '16px', color: '#333', marginBottom: '4px' }}>New Request</div>
                                <div style={{ fontSize: '13px', color: '#666' }}>Request new permissions for your team members</div>
                            </button>
                            <button type="button"
                                onClick={() => { setRequestType('renewal'); setStep(3); }}
                                style={{ padding: '24px 20px', borderRadius: '12px', cursor: 'pointer', border: '2px solid #e2e6ea', background: '#fff', textAlign: 'left', transition: 'all 0.2s' }}
                                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#0073bb'; e.currentTarget.style.background = '#f0f8ff'; }}
                                onMouseOut={(e) => { e.currentTarget.style.borderColor = '#e2e6ea'; e.currentTarget.style.background = '#fff'; }}
                            >
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔄</div>
                                <div style={{ fontWeight: '700', fontSize: '16px', color: '#333', marginBottom: '4px' }}>{useCaseType === 'approved' ? 'Rebaseline' : 'Renewal Request'}</div>
                                <div style={{ fontSize: '13px', color: '#666' }}>{useCaseType === 'approved' ? 'Rebaseline existing permissions for your team' : 'Renew existing permissions for your team members'}</div>
                            </button>
                        </div>
                    </div>
            </PageLayout>
        );
    }

    // ════════════════════════════════════════
    // STEP 3: Requester Alias + L3 Check
    // ════════════════════════════════════════
    if (step === 3) {
        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle={`${useCaseType === 'approved' ? 'Approved' : 'Unlisted'} — ${requestType === 'new' ? 'New Request' : 'Renewal'}`}>
                    <button type="button" onClick={() => setStep(2)}
                        style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#666', marginBottom: '16px' }}>
                        ← Back
                    </button>

                    <div className="section">
                        <div className="section-title">👤 Requester Verification</div>
                        <div className="form-group">
                            <label>Your Alias <span className="required">*</span></label>
                            <input type="text" value={requesterAlias}
                                onChange={(e) => { setRequesterAlias(e.target.value.toLowerCase().trim()); setLevelCheckDone(false); setLevelCheckError(''); }}
                                onBlur={(e) => setRequesterAlias(normalizeAlias(e.target.value))}
                                placeholder="e.g., johndoe"
                                style={{ textTransform: 'lowercase', backgroundColor: userAlias ? '#f0f4f0' : undefined }}
                                readOnly={!!userAlias} />
                            <div className="hint">{userAlias ? '🔒 Authenticated via Midway' : 'Your Amazon login alias'}</div>
                        </div>

                        {/* Verify button */}
                        {!levelCheckDone && (
                            <button type="button" onClick={checkRequesterLevel}
                                disabled={isCheckingLevel || !requesterAlias.trim()}
                                style={{
                                    padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: '600',
                                    cursor: isCheckingLevel ? 'wait' : 'pointer', border: 'none',
                                    background: '#ff9900', color: '#000', width: '100%', marginTop: '8px'
                                }}>
                                {isCheckingLevel ? '⏳ Verifying...' : '🔍 Verify & Continue'}
                            </button>
                        )}

                        {/* L3 blocked */}
                        {levelCheckError === 'blocked' && (
                            <div style={{ marginTop: '16px', padding: '16px', borderRadius: '10px', backgroundColor: '#ffeef0', border: '1px solid #ffc1c8' }}>
                                <div style={{ fontWeight: '700', fontSize: '15px', color: '#d13212', marginBottom: '8px' }}>
                                    ❌ Access Restricted
                                </div>
                                <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>
                                    Your job level (L{requesterJobLevel}) is below the minimum requirement (L4+). 
                                    Please reach out to your manager to submit this request on your behalf.
                                </p>
                            </div>
                        )}

                        {/* Other errors — allow continue if authenticated via Midway */}
                        {levelCheckError && levelCheckError !== 'blocked' && (
                            <div style={{ marginTop: '12px' }}>
                                <div style={{ padding: '12px 14px', borderRadius: '8px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e', marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                        <span style={{ fontSize: '16px' }}>⚠️</span>
                                        <div>
                                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Verification unavailable</div>
                                            <div>{levelCheckError}</div>
                                            {userAlias && (
                                                <div style={{ marginTop: '6px', fontSize: '12px', color: '#555' }}>
                                                    Since you're authenticated via Midway, you can continue without verification.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button type="button" onClick={checkRequesterLevel}
                                        disabled={isCheckingLevel}
                                        style={{
                                            flex: 1, padding: '10px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                                            cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#333'
                                        }}>
                                        🔄 Retry
                                    </button>
                                    {userAlias && (
                                        <button type="button" onClick={() => { 
                                            setRequesterJobLevel(null); setLevelCheckDone(true); setLevelCheckError(''); 
                                            if (requestType === 'renewal') { fetchRenewalRequests(); }
                                            listTeamConfigs().then(result => {
                                                if (result && result.teams) setAllTeamConfigs(result.teams.filter(t => t && t.active === true));
                                            }).catch(() => {});
                                            setStep(4); 
                                        }}
                                            style={{
                                                flex: 2, padding: '10px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                                                cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000'
                                            }}>
                                            Continue Anyway →
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Success — continue */}
                        {levelCheckDone && !levelCheckError && (
                            <div style={{ marginTop: '16px' }}>
                                <div style={{ padding: '12px 16px', borderRadius: '8px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '18px' }}>✅</span>
                                    <div>
                                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#166534' }}>Verified: {requesterAlias}{requesterJobLevel ? ` (L${requesterJobLevel})` : ''}</div>
                                        <div style={{ fontSize: '12px', color: '#555' }}>{requesterJobLevel ? 'Job level meets minimum requirement' : 'Authenticated via Midway'}</div>
                                    </div>
                                </div>
                                <button type="button" onClick={() => { 
                                    if (requestType === 'renewal') { fetchRenewalRequests(); }
                                    // Fetch team configs for dynamic dropdowns
                                    listTeamConfigs().then(result => {
                                        if (result && result.teams) setAllTeamConfigs(result.teams.filter(t => t && t.active === true));
                                    }).catch(() => {});
                                    setStep(4); 
                                }}
                                    style={{ padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000', width: '100%' }}>
                                    Continue →
                                </button>
                            </div>
                        )}
                    </div>
            </PageLayout>
        );
    }

    // ════════════════════════════════════════
    // STEP 4: Function / Org / Team
    // ════════════════════════════════════════
    if (step === 4) {
        const approvalLink = (teamConfig && teamConfig.approvalLink) || getApprovalLink(Array.isArray(functionDropdown) ? functionDropdown.join(', ') : functionDropdown, orgName, teamValue);

        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle="Select your team">
                    <button type="button" onClick={() => setStep(3)}
                        style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#666', marginBottom: '16px' }}>
                        ← Back
                    </button>

                    {/* Breadcrumb badges */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: useCaseType === 'approved' ? '#dcfce7' : '#fff3cd', color: useCaseType === 'approved' ? '#166534' : '#856404' }}>
                            {useCaseType === 'approved' ? '✅ Approved' : '📋 Unlisted'}
                        </span>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: '#e0e7ff', color: '#4338ca' }}>
                            {requestType === 'new' ? '🆕 New' : '🔄 Renewal'}
                        </span>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f0fdf4', color: '#166534' }}>
                            👤 {requesterAlias}{requesterJobLevel ? ` (L${requesterJobLevel})` : ''}
                        </span>
                    </div>

                    {/* ═══ Renewal Lookup ═══ */}
                    {requestType === 'renewal' && !originalRequestId && (
                        <div className="section" style={{ borderLeft: '4px solid #0073bb', paddingLeft: '16px', marginBottom: '16px' }}>
                            <div className="section-title" style={{ color: '#0073bb' }}>🔄 Renewal — Load Previous Request</div>
                            
                            {isFetchingRenewal && (
                                <div style={{ padding: '12px', fontSize: '13px', color: '#0073bb' }}>⏳ Loading previous requests...</div>
                            )}

                            {renewalError && (
                                <div style={{ padding: '10px 14px', borderRadius: '6px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e', marginBottom: '12px' }}>
                                    ⚠️ {renewalError}
                                </div>
                            )}

                            {/* Previous requests list */}
                            {!isFetchingRenewal && renewalRequests.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ fontSize: '13px', color: '#555', marginBottom: '8px' }}>Select a previous request to renew:</div>
                                    <div style={{ maxHeight: '280px', overflowY: 'auto', borderRadius: '8px', border: '1px solid #e2e6ea' }}>
                                        {renewalRequests.slice(0, 15).map((req, idx) => {
                                            // Calculate expiry status
                                            const expiryDate = req.accessEndDate;
                                            let expiryLabel = '';
                                            let expiryColor = '#666';
                                            let expiryBg = 'transparent';
                                            if (expiryDate) {
                                                const daysLeft = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
                                                if (daysLeft < 0) {
                                                    expiryLabel = '❌ Expired';
                                                    expiryColor = '#dc2626';
                                                    expiryBg = '#fef2f2';
                                                } else if (daysLeft <= 7) {
                                                    expiryLabel = `⚠️ ${daysLeft}d left`;
                                                    expiryColor = '#dc2626';
                                                    expiryBg = '#fef2f2';
                                                } else if (daysLeft <= 15) {
                                                    expiryLabel = `🔔 ${daysLeft}d left`;
                                                    expiryColor = '#f59e0b';
                                                    expiryBg = '#fffbeb';
                                                } else if (daysLeft <= 30) {
                                                    expiryLabel = `📅 ${daysLeft}d left`;
                                                    expiryColor = '#3b82f6';
                                                    expiryBg = '#eff6ff';
                                                } else {
                                                    expiryLabel = `${daysLeft}d left`;
                                                    expiryColor = '#666';
                                                }
                                            }
                                            const hasRenewal = !!req.renewalRequestId;

                                            return (
                                            <div key={req.requestId} 
                                                onClick={() => !hasRenewal && loadRenewalData(req.requestId)}
                                                style={{
                                                    padding: '12px 14px', cursor: hasRenewal ? 'not-allowed' : 'pointer', borderBottom: '1px solid #f0f0f0',
                                                    background: hasRenewal ? '#f9fafb' : idx % 2 === 0 ? '#fff' : '#f8f9fa',
                                                    opacity: hasRenewal ? 0.6 : 1,
                                                    transition: 'background 0.15s'
                                                }}
                                                onMouseOver={(e) => { if (!hasRenewal) e.currentTarget.style.background = '#eef6ff'; }}
                                                onMouseOut={(e) => { if (!hasRenewal) e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8f9fa'; }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#333' }}>
                                                            {req.teamLabel || req.team || req.functionDropdown || 'Unknown Team'} — {req.useCase || 'N/A'}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                            {req.requestId} · {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : 'Unknown date'}
                                                            {req.totalUsers && ` · ${req.totalUsers} users`}
                                                            {expiryDate && <span style={{ marginLeft: '8px', color: '#888' }}>· Expires: {expiryDate}</span>}
                                                        </div>
                                                        {hasRenewal && (
                                                            <div style={{ fontSize: '11px', color: '#22c55e', marginTop: '3px', fontWeight: '500' }}>
                                                                ✅ Renewal already submitted ({req.renewalRequestId})
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                                        {/* Expiry badge */}
                                                        {expiryDate && !hasRenewal && (
                                                            <span style={{
                                                                padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                                                                backgroundColor: expiryBg, color: expiryColor, border: `1px solid ${expiryColor}22`,
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                {expiryLabel}
                                                            </span>
                                                        )}
                                                        {/* Status badge */}
                                                        <span style={{
                                                            padding: '3px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                                                            backgroundColor: (req.approvalStatus === 'APPROVED') ? '#dcfce7' : (req.approvalStatus === 'DENIED') ? '#ffeef0' : '#fff3cd',
                                                            color: (req.approvalStatus === 'APPROVED') ? '#166534' : (req.approvalStatus === 'DENIED') ? '#d13212' : '#856404',
                                                        }}>
                                                            {req.approvalStatus || 'PENDING'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Manual Request ID input */}
                            {!isFetchingRenewal && (
                                <div style={{ marginTop: '12px' }}>
                                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>Or enter a Request ID manually:</div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input type="text" value={renewalInput} onChange={(e) => setRenewalInput(e.target.value)}
                                            placeholder="e.g., UFS-D9E93903"
                                            style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                                        <button type="button" onClick={() => { if (renewalInput.trim()) loadRenewalData(renewalInput.trim()); }}
                                            disabled={!renewalInput.trim() || isFetchingRenewal}
                                            style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none', background: '#0073bb', color: '#fff' }}>
                                            Load
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Skip renewal — proceed as new */}
                            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid #e2e6ea' }}>
                                <button type="button" onClick={() => setOriginalRequestId('skip')}
                                    style={{ fontSize: '13px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                    Skip — fill in manually instead
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Renewal loaded banner */}
                    {requestType === 'renewal' && originalRequestId && originalRequestId !== 'skip' && (
                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', marginBottom: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                                <span style={{ fontWeight: '600', color: '#0073bb' }}>🔄 Renewing:</span> {originalRequestId}
                                <span style={{ color: '#666', marginLeft: '8px' }}>— Team, permissions, and users pre-filled. You can modify as needed.</span>
                            </div>
                            <button type="button" onClick={() => { setOriginalRequestId(''); setRenewalInput(''); }}
                                style={{ fontSize: '12px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                Change
                            </button>
                        </div>
                    )}

                    {/* Show team form only when renewal is loaded or it's a new request or skipped */}
                    {(requestType === 'new' || originalRequestId) && (
                    <div className="section">
                        <div className="section-title">📋 Select Your Team</div>

                        {/* Team selection */}
                        <div className="form-group">
                            <label>Select Team <span className="required">*</span></label>
                            {isRenewalRequest && (
                                <div style={{ marginBottom: '8px', padding: '8px 12px', borderRadius: '6px', background: '#eef6ff', border: '1px solid #b8d4f0', fontSize: '12px', color: '#0c5460' }}>
                                    🔄 Renewal — team, function and permissions are locked to match the original request.
                                </div>
                            )}
                            <select value={teamValue} disabled={isRenewalRequest}
                                onChange={(e) => {
                                    const opt = e.target.options[e.target.selectedIndex];
                                    const selectedConfig = allTeamConfigs.find(c => c.teamLabel === opt.dataset.label);
                                    setTeamLabel(opt.dataset.label || '');
                                    setTeamValue(e.target.value);
                                    setFunctionDropdown(selectedConfig?.functionDropdown ? [selectedConfig.functionDropdown] : []);
                                    setOrgName(selectedConfig?.orgName || '');
                                }}
                                style={isRenewalRequest ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : undefined}>
                                <option value="">-- Select Team --</option>
                                {allTeamConfigs.map(c => (
                                    <option key={c.teamLabel} value={c.teamLabel.toLowerCase().replace(/\s+/g, '_')} data-label={c.teamLabel}>{c.teamLabel}</option>
                                ))}
                            </select>
                        </div>

                        {/* Function & Org — auto-filled from config, read-only */}
                        {teamValue && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' }}>
                                <div className="form-group">
                                    <label>Function <span className="required">*</span></label>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                        {functionOptions.map(opt => (
                                            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '6px', border: `1px solid ${functionDropdown.includes(opt.value) ? '#ff9900' : '#ddd'}`, background: functionDropdown.includes(opt.value) ? '#fffbf0' : '#fff', cursor: isRenewalRequest ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '600', opacity: isRenewalRequest ? 0.7 : 1 }}>
                                                <input type="checkbox" checked={functionDropdown.includes(opt.value)} disabled={isRenewalRequest} onChange={() => toggleFunction(opt.value)} style={{ width: '14px', height: '14px' }} />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Org Name <span className="required">*</span></label>
                                    <input type="text" value={orgName} readOnly
                                        style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                        placeholder="Auto-filled from team config" />
                                </div>
                            </div>
                        )}

                        {/* Valid Departments */}
                        {teamValue && (() => {
                            const selectedConfig = allTeamConfigs.find(c => c.teamLabel.toLowerCase().replace(/\s+/g, '_') === teamValue);
                            const depts = selectedConfig?.orgAliases || [];
                            return depts.length > 0 ? (
                                <div style={{ padding: '8px 12px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', marginTop: '8px', fontSize: '12px', color: '#555' }}>
                                    <span style={{ fontWeight: '600' }}>Valid Departments:</span> {depts.join(', ')}
                                </div>
                            ) : null;
                        })()}

                        {/* Approval link banner */}
                        {teamValue && approvalLink && (
                            <div style={{
                                padding: '12px 16px', borderRadius: '8px', marginTop: '12px',
                                backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
                                display: 'flex', alignItems: 'center', gap: '10px'
                            }}>
                                <span style={{ fontSize: '18px' }}>✅</span>
                                <div>
                                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#166534' }}>
                                        Pre-Approved Use Case
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>
                                        This use case has been pre-approved by leadership.{' '}
                                        <a href={approvalLink} target="_blank" rel="noopener noreferrer"
                                            style={{ color: '#0073bb', textDecoration: 'underline', fontWeight: '600' }}>
                                            View Approval ↗
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Loading config */}
                        {isLoadingConfig && (
                            <div style={{ marginTop: '12px', fontSize: '13px', color: '#666' }}>⏳ Loading team permissions...</div>
                        )}
                        {configError && (
                            <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e' }}>
                                ⚠️ {configError}
                            </div>
                        )}

                        {/* Continue button */}
                        {teamValue && !isLoadingConfig && (
                            <button type="button" onClick={async () => {
                                setIsLoadingConfig(true);
                                setConfigError('');
                                try {
                                    // Skip API call on localhost
                                    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                                        setTeamConfig({ found: true, permissions: [], approvalLink: '' });
                                    } else {
                                        const config = await getTeamConfig(teamLabel);
                                        setTeamConfig(config);
                                    }
                                    setStep(5);
                                } catch (err) {
                                    setConfigError('Could not load team config: ' + err.message);
                                } finally {
                                    setIsLoadingConfig(false);
                                }
                            }}
                                style={{
                                    padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: '600',
                                    cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000',
                                    width: '100%', marginTop: '16px'
                                }}>
                                Continue to Permissions →
                            </button>
                        )}
                    </div>
                    )}
            </PageLayout>
        );
    }

    // ════════════════════════════════════════
    // STEP 5: Permission Selector
    // ════════════════════════════════════════
    if (step === 5) {
        // RENEWAL: permissions are locked to the original. Render a READ-ONLY summary of the
        // pre-filled permissions (built from selectedObjects, with a name fallback so it works
        // even if a permission id isn't in the current catalog) + a clear Continue button.
        // This avoids the interactive catalog's team-config filtering and collapse behaviour,
        // which could hide the locked permission and leave the user with no Continue.
        if (isRenewalRequest) {
            const renewalPermNames = (selectedObjects && selectedObjects.length > 0)
                ? selectedObjects.map(p => p.name)
                : (previousSelectedPermissionNames || []);
            // Allow continue if we have either resolved permission IDs OR the original
            // permission names (covers the case where an id isn't in the current catalog).
            const canContinueRenewal = selectedPermissions.size > 0 || renewalPermNames.length > 0;
            return (
                <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle="Renewal — locked permissions">
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: '#e0e7ff', color: '#4338ca' }}>🔄 Renewal</span>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f0fdf4', color: '#166534' }}>👤 {requesterAlias}</span>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f8f9fa', color: '#333' }}>🏢 {teamLabel}</span>
                    </div>
                    <div className="section">
                        <div className="section-title">📋 Permissions (locked)</div>
                        <div style={{ marginBottom: '14px', padding: '10px 14px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', fontSize: '13px', color: '#0c5460' }}>
                            🔄 <strong>Renewal</strong> — these permissions are locked to match the original request ({originalRequestId}). To request different permissions, submit a new request instead.
                        </div>
                        {renewalPermNames.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {renewalPermNames.map((nm, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '8px', border: '1px solid #bbf7d0', background: '#f0fdf4' }}>
                                        <span style={{ fontSize: '14px' }}>🔒</span>
                                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>{nm}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ padding: '12px 14px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e' }}>
                                ⚠️ Could not load the original permissions for this request. Please submit a new request, or contact the PAM team.
                            </div>
                        )}
                    </div>
                    <button type="button" onClick={() => setStep(useCaseType === 'unlisted' ? 55 : 57)} disabled={!canContinueRenewal}
                        style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: canContinueRenewal ? 'pointer' : 'not-allowed', border: 'none', background: canContinueRenewal ? '#ff9900' : '#e2e6ea', color: canContinueRenewal ? '#000' : '#888', marginTop: '16px' }}>
                        {canContinueRenewal ? 'Continue →' : 'No permissions loaded'}
                    </button>
                </PageLayout>
            );
        }
        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle="Select permissions">
                    <button type="button" onClick={() => setStep(1)}
                        style={{ padding: '10px 18px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: '2px solid #0073bb', background: '#f0f8ff', color: '#0073bb', marginBottom: '16px' }}>
                        ← Back to Team
                    </button>

                    {/* Breadcrumb badges */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: useCaseType === 'approved' ? '#dcfce7' : '#fff3cd', color: useCaseType === 'approved' ? '#166534' : '#856404' }}>
                            {useCaseType === 'approved' ? '✅ Approved Use Case' : '📋 Unlisted Use Case'}
                        </span>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: '#e0e7ff', color: '#4338ca' }}>
                            {requestType === 'new' ? '🆕 New Request' : '🔄 Renewal'}
                        </span>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f0fdf4', color: '#166534' }}>
                            👤 {requesterAlias}
                        </span>
                        <span style={{ padding: '4px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f8f9fa', color: '#333' }}>
                            🏢 {teamLabel}
                        </span>
                    </div>

                    <div className="section">
                        <div className="section-title">📋 Select Permissions</div>
                        {isRenewalRequest && (
                            <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', fontSize: '13px', color: '#0c5460' }}>
                                🔄 <strong>Renewal</strong> — permissions are locked to match the original request ({originalRequestId}). Review, then continue to edit users and submit. To request different permissions, submit a new request instead.
                            </div>
                        )}
                        <div style={{ marginBottom: '16px' }}>
                            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search permissions by name or description..."
                                style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px' }} />
                        </div>

                        {/* Permission not listed link — only show for approved flow, and NOT on renewal
                            (switching use case would change permissions, which a renewal can't do). */}
                        {useCaseType === 'approved' && !isRenewalRequest && (
                        <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e' }}>
                            ⚠️ Permission not listed?{' '}
                            <button type="button" onClick={() => { setUseCaseType('unlisted'); setStep(1); }}
                                style={{ background: 'none', border: 'none', color: '#0073bb', textDecoration: 'underline', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                                Switch to Unlisted Use Case →
                            </button>
                        </div>
                        )}

                        {/* Unlisted info banner */}
                        {useCaseType === 'unlisted' && (
                        <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', background: '#f0f8ff', border: '1px solid #b8d4f0', fontSize: '13px', color: '#0c5460' }}>
                            📋 Unlisted Use Case — All T178 permissions are shown. Select the ones you need. Approval routing: CRP→L8, HRP→L7, LRP/MRP→L6.{' '}
                            <a href="https://w.amazon.com/bin/view/Tenant_178/" target="_blank" rel="noopener noreferrer" style={{ color: '#0073bb', fontWeight: '600' }}>T178 Permissions Wiki ↗</a>
                        </div>
                        )}

                        {/* Permissions grouped by Risk Type */}
                        {riskTypeCategories.map(rtCat => {
                            // For unlisted: show ALL permissions. For approved: filter by team config.
                            let availablePerms;
                            if (useCaseType === 'unlisted') {
                                availablePerms = permissions;
                            } else if (teamConfig && teamConfig.found && teamConfig.permissions && teamConfig.permissions.length > 0) {
                                const configPermIds = teamConfig.permissions.map(p => p.id);
                                availablePerms = permissions.filter(p => configPermIds.includes(p.id));
                            } else {
                                availablePerms = getApprovedPermissionsForTeam(teamValue);
                            }
                            const catPerms = filterPermissions(availablePerms.filter(p => p.riskType === rtCat.id));
                            if (catPerms.length === 0) return null;
                            const isCollapsed = collapsedCategories.has(rtCat.id);
                            const selectedInCat = catPerms.filter(p => selectedPermissions.has(p.id)).length;
                            const config = riskTypeConfig[rtCat.id];

                            return (
                                <div key={rtCat.id} style={{ marginBottom: '12px', borderRadius: '10px', border: '1px solid #e2e6ea', overflow: 'hidden', background: '#fff' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f8f9fa', cursor: 'pointer', borderBottom: isCollapsed ? 'none' : '1px solid #e2e6ea' }}
                                        onClick={() => toggleCategory(rtCat.id)}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ fontSize: '14px' }}>{isCollapsed ? '▶' : '▼'}</span>
                                            <span style={{ fontSize: '16px' }}>{rtCat.icon}</span>
                                            <span style={{ fontWeight: '700', fontSize: '14px', color: '#333' }}>{rtCat.name}</span>
                                            <span style={{ fontSize: '12px', color: '#888' }}>({catPerms.length})</span>
                                            <span style={{ fontSize: '11px', color: '#666', padding: '2px 8px', borderRadius: '8px', background: '#f3f4f6' }}>{config.accessDays} days</span>
                                            {selectedInCat > 0 && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534' }}>{selectedInCat} selected</span>}
                                        </div>
                                    </div>
                                    {!isCollapsed && catPerms.map(perm => {
                                        const isSelected = selectedPermissions.has(perm.id);
                                        // Renewal: permissions are locked. Hide unselected perms so the user
                                        // only sees what they're renewing; selected ones show but can't be toggled.
                                        if (isRenewalRequest && !isSelected) return null;
                                        return (
                                            <div key={perm.id}
                                                style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: isSelected ? '#f0fdf4' : '#fff', cursor: isRenewalRequest ? 'default' : 'pointer', opacity: isRenewalRequest ? 0.85 : 1 }}
                                                onClick={() => { if (!isRenewalRequest) togglePermission(perm.id); }}>
                                                <input type="checkbox" checked={isSelected} disabled={isRenewalRequest} onChange={() => {}} style={{ width: '18px', height: '18px', marginTop: '2px', cursor: isRenewalRequest ? 'not-allowed' : 'pointer' }} />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                                                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>{perm.name}</span>
                                                        <span style={{ padding: '1px 6px', borderRadius: '8px', fontSize: '10px', fontWeight: '600', backgroundColor: '#f3f4f6', color: '#666' }}>{perm.riskType === 'CRP' ? 'L8' : perm.riskType === 'HRP' ? 'L7' : 'L6'}</span>
                                                        {perm.restrictions && <span style={{ padding: '2px 8px', borderRadius: '8px', fontSize: '11px', color: '#92400e', fontWeight: '600', backgroundColor: '#fef3c7', border: '1px solid #fde68a' }}>⚠️ {perm.restrictions}</span>}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#666' }}>{perm.description}</div>
                                                </div>
                                                <a href={perm.permissionLink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                                                    style={{ padding: '4px 10px', borderRadius: '5px', fontSize: '11px', fontWeight: '600', color: '#0073bb', textDecoration: 'none', border: '1px solid #b8d4f0', background: '#f0f8ff', whiteSpace: 'nowrap' }}>
                                                    View ↗
                                                </a>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>

                {/* Selected-permission CHIPS — grouped by risk type, removable (parity with T321).
                    Lets the user see exactly what's picked and deselect any one without scrolling
                    back to its checkbox. */}
                {selectedPermissions.size > 0 && (
                    <div style={{ marginTop: '4px', marginBottom: '8px' }}>
                        {selectedRiskTypes.map(rt => {
                            const rtPerms = (riskGroups[rt] || []);
                            if (rtPerms.length === 0) return null;
                            return (
                                <div key={rt} style={{ marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                        <span style={{ ...getRiskBadgeStyle(rt), fontSize: '10px' }}>{rt}</span>
                                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#555' }}>({rtPerms.length})</span>
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                        {rtPerms.map(perm => (
                                            <span key={perm.id} onClick={() => { if (!isRenewalRequest) togglePermission(perm.id); }} title={isRenewalRequest ? 'Locked for renewal' : 'Click to remove'}
                                                style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#374151', cursor: isRenewalRequest ? 'default' : 'pointer' }}>
                                                {perm.name}{isRenewalRequest ? '' : ' ✕'}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Sticky cart */}
                {selectedPermissions.size > 0 && (() => {
                    const canContinue = selectedPermissions.size > 0;
                    // Each risk type present becomes its own approval (per-RT routing), so the
                    // approval count = number of distinct selected risk types (parity with T321's
                    // "-> N approval(s)" readout).
                    const approvalCount = selectedRiskTypes.length;

                    return (
                    <div style={{ position: 'fixed', bottom: '52px', left: 0, right: 0, zIndex: 1001, background: 'linear-gradient(135deg, #1a365d, #2a4a7f)', borderTop: '2px solid #ff9900', padding: '14px 24px', boxShadow: '0 -4px 20px rgba(0,0,0,0.15)' }}>
                        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <span style={{ color: '#fff', fontWeight: '700', fontSize: '14px' }}>
                                    🛒 {selectedPermissions.size} selected
                                </span>
                                {selectedRiskTypes.map(rt => (
                                    <span key={rt} style={{ ...getRiskBadgeStyle(rt), fontSize: '10px' }}>
                                        {rt}: {riskGroups[rt]?.length || 0}
                                    </span>
                                ))}
                                <span style={{ fontSize: '11px', color: '#ffd699', fontWeight: '600' }}>
                                    → {approvalCount} approval{approvalCount !== 1 ? 's' : ''}
                                </span>
                                {hasCRP(selectedArray) && (
                                    <span style={{ fontSize: '11px', color: '#ffc1c8' }}>⚠️ UFS validation required</span>
                                )}
                            </div>
                            <button type="button" onClick={() => setStep(useCaseType === 'unlisted' ? 55 : 57)} disabled={!canContinue}
                                style={{ padding: '8px 24px', borderRadius: '6px', fontSize: '14px', fontWeight: '700', cursor: canContinue ? 'pointer' : 'not-allowed', border: 'none', background: canContinue ? '#ff9900' : '#666', color: canContinue ? '#000' : '#ccc' }}>
                                Continue →
                            </button>
                        </div>
                    </div>
                    );
                })()}
            </PageLayout>
        );
    }

    // ════════════════════════════════════════
    // STEP 55: Use Case (Unlisted only)
    // ════════════════════════════════════════
    if (step === 55) {
        const useCasesFilled = Object.keys(riskGroups).filter(rt => useCaseByRT[rt] && useCaseByRT[rt].trim()).length;
        const useCasesTotal = Object.keys(riskGroups).length;
        const canContinue = useCasesFilled === useCasesTotal;

        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle="Use Case Justification">
                <button type="button" onClick={() => setStep(5)}
                    style={{ padding: '10px 18px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: '2px solid #0073bb', background: '#f0f8ff', color: '#0073bb', marginBottom: '16px' }}>
                    ← Back to Permissions
                </button>

                <div className="section">
                    <div className="section-title">📝 Use Case (per Permission Group)</div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                        Describe why your team needs each permission group. All fields are required.
                    </div>
                    {Object.entries(riskGroups).map(([rt, perms]) => {
                        const rtPermNames = perms.map(p => p.name).join(', ');
                        const rtUseCase = useCaseByRT[rt] || '';
                        return (
                            <div key={rt} style={{ marginBottom: '14px', padding: '14px 16px', borderRadius: '8px', border: `1px solid ${rtUseCase.trim() ? '#bbf7d0' : '#e2e6ea'}`, background: rtUseCase.trim() ? '#f0fdf4' : '#fff' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                    <span style={getRiskBadgeStyle(rt)}>{rt}</span>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{rtPermNames}</span>
                                    {rtUseCase.trim() && <span style={{ fontSize: '12px', color: '#166534', marginLeft: 'auto' }}>✅ Done</span>}
                                </div>
                                <textarea
                                    value={rtUseCase}
                                    onChange={(e) => setUseCaseByRT(prev => ({ ...prev, [rt]: e.target.value }))}
                                    placeholder="Type your use case here following the template above..."
                                    rows={4}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #dde1e6', fontSize: '14px', resize: 'vertical' }}
                                />
                                {!rtUseCase.trim() && (
                                    <div style={{ marginTop: '8px', padding: '10px 14px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', fontSize: '12px', color: '#888', lineHeight: '1.7', fontStyle: 'italic' }}>
                                        <strong style={{ color: '#666', fontStyle: 'normal' }}>Template:</strong> As an <strong>[job family(s)/job title]</strong> working in <strong>[org]</strong> <strong>[function/process]</strong> I need to use {rtPermNames} <strong>[insert action]</strong> to look up/take action <strong>[what information are you focusing on - fields/widgets/etc]</strong>. I use this information to <strong>[X - what do you do with the information, decisions, tools, processes]</strong>. Without this access <strong>[Y - what would be the impact if you did not have this access - quantify monetary loss, productivity loss, etc]</strong>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                    <span style={{ fontSize: '13px', color: canContinue ? '#166534' : '#92400e', fontWeight: '600' }}>
                        {canContinue ? '✅ All use cases provided' : `⚠️ ${useCasesFilled}/${useCasesTotal} completed`}
                    </span>
                    <button type="button" onClick={() => setStep(56)} disabled={!canContinue}
                        style={{ padding: '12px 24px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: canContinue ? 'pointer' : 'not-allowed', border: 'none', background: canContinue ? '#ff9900' : '#e2e6ea', color: canContinue ? '#000' : '#888' }}>
                        Continue to Leaders →
                    </button>
                </div>
                {canContinue && (
                    <div style={{ marginTop: '10px', padding: '10px 14px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e', lineHeight: '1.5' }}>
                        ⚠️ <strong>Note:</strong> If your use case is not as per the template, it will take longer to grant access as we will have follow-up questions to understand your use case.
                    </div>
                )}
            </PageLayout>
        );
    }

    // ════════════════════════════════════════
    // STEP 56: Leaders (Unlisted only)
    // ════════════════════════════════════════
    if (step === 56) {
        const hasLeaders = selectedRiskTypes.every(rt => leadersByRT[rt]);
        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle="Leadership Approval">
                <button type="button" onClick={() => setStep(55)}
                    style={{ padding: '10px 18px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: '2px solid #0073bb', background: '#f0f8ff', color: '#0073bb', marginBottom: '16px' }}>
                    ← Back to Use Case
                </button>

                <div className="section">
                    <div className="section-title">👔 Leadership Approval (per Risk Type)</div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                        Each risk type routes to the appropriate leader: CRP→L8, HRP→L7, LRP/MRP→L6.
                    </div>
                    <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', marginBottom: '14px', fontSize: '13px', color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <span style={{ fontSize: '16px', lineHeight: '1' }}>⚠️</span>
                        <span><strong>Important:</strong> Please ensure you select the correct leader for approval. If the wrong leader is selected, your request will be rejected.</span>
                    </div>
                    <button type="button" onClick={async () => {
                        setIsLookingUpLeader(true);
                        setLeaderLookupError('');
                        try {
                            for (const rt of selectedRiskTypes) {
                                const targetLevel = rt === 'CRP' ? 8 : rt === 'HRP' ? 7 : 6;
                                const result = await lookupManager(requesterAlias, targetLevel);
                                // Capture the full management chain for this RT so manual mode can
                                // offer a "select from your chain" dropdown + show the full chain.
                                // (lookupManager already returns managerChain with per-member jobLevel.)
                                if (result && Array.isArray(result.managerChain)) {
                                    setChainByRT(prev => ({ ...prev, [rt]: result.managerChain }));
                                }
                                if (result && result.l8Manager && result.l8Manager.alias) {
                                    setLeadersByRT(prev => ({ ...prev, [rt]: result.l8Manager }));
                                } else if (result && result.managerChain) {
                                    // Fallback: find first manager in chain at target level
                                    const match = result.managerChain.find(m => m.jobLevel && m.jobLevel >= targetLevel);
                                    if (match) {
                                        setLeadersByRT(prev => ({ ...prev, [rt]: match }));
                                    }
                                }
                            }
                        } catch (err) {
                            setLeaderLookupError(err.message);
                        } finally {
                            setIsLookingUpLeader(false);
                        }
                    }}
                        disabled={isLookingUpLeader}
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#ff9900', color: '#000', marginBottom: '16px' }}>
                        {isLookingUpLeader ? '⏳ Looking up...' : '🔍 Auto-detect Leaders for All Risk Types'}
                    </button>
                    <div style={{ textAlign: 'center', marginBottom: '16px', marginTop: '-8px' }}>
                        <button type="button" onClick={async () => {
                            const flags = {}; selectedRiskTypes.forEach(rt => { flags[rt] = true; }); setManualLeaderRT(flags);
                            // Warm the management chain so the "select from your chain" dropdown is
                            // available even when the user goes straight to manual (skips auto-detect).
                            // Best-effort: never blocks manual entry if the lookup fails.
                            try {
                                for (const rt of selectedRiskTypes) {
                                    if (chainByRT[rt]) continue; // already have it (e.g. from auto-detect)
                                    const targetLevel = rt === 'CRP' ? 8 : rt === 'HRP' ? 7 : 6;
                                    const result = await lookupManager(requesterAlias, targetLevel);
                                    if (result && Array.isArray(result.managerChain)) {
                                        setChainByRT(prev => ({ ...prev, [rt]: result.managerChain }));
                                    }
                                }
                            } catch (e) { /* manual entry still works without the chain */ }
                        }}
                            style={{ fontSize: '14px', color: '#92400e', background: '#fffbeb', border: '2px solid #fde68a', cursor: 'pointer', padding: '10px 24px', borderRadius: '8px', fontWeight: '600', width: '100%' }}>
                            ✏️ Or Enter Manager Manually
                        </button>
                    </div>
                    {leaderLookupError && <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e', marginBottom: '12px' }}>⚠️ {leaderLookupError}</div>}

                    {selectedRiskTypes.map(rt => {
                        const requiredLevel = rt === 'CRP' ? 'L8' : rt === 'HRP' ? 'L7' : 'L6';
                        const leader = leadersByRT[rt];
                        const isManual = manualLeaderRT[rt];
                        return (
                            <div key={rt} style={{ marginBottom: '10px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e6ea', background: leader ? '#f0fdf4' : '#fff' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={getRiskBadgeStyle(rt)}>{rt}</span>
                                    <span style={{ fontSize: '13px', color: '#666' }}>→ {requiredLevel} required</span>
                                    {leader && (
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                                            <span style={{ fontSize: '13px', fontWeight: '600', color: leader.unverified ? '#92400e' : '#166534' }}>
                                                {leader.unverified ? `⚠️ ${leader.alias} (unverified)` : `✅ ${leader.alias} (L${leader.jobLevel})`}
                                            </span>
                                            {/* Change/Override — e.g. if the auto-detected leader is on leave, substitute a
                                                different qualified leader (goes through the same level + self checks). */}
                                            <button type="button"
                                                onClick={() => {
                                                    setLeadersByRT(prev => { const n = { ...prev }; delete n[rt]; return n; });
                                                    setManualLeaderRT(prev => ({ ...prev, [rt]: true }));
                                                    setManualLeaderInputs(prev => ({ ...prev, [rt]: '' }));
                                                    setManualLeaderCheck(prev => ({ ...prev, [rt]: {} }));
                                                }}
                                                style={{ fontSize: '11px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                                                Change
                                            </button>
                                        </span>
                                    )}
                                </div>
                                {!leader && !isManual && null}
                                {isManual && !leader && (() => {
                                    const reqNum = parseInt(requiredLevel.replace('L', ''), 10); // 6/7/8
                                    const chk = manualLeaderCheck[rt] || {};
                                    // Validate + set a leader for this RT. Shared by BOTH the "select from
                                    // your chain" dropdown and the manual text input, so a chain pick is
                                    // NOT blind-trusted — it goes through the same level + self checks.
                                    const validateAndSetLeader = async (rawAlias) => {
                                        // Normalize to a bare login: users often paste "alias@amazon.com",
                                        // but the CSV/verification lookup and Approvals expect just "alias".
                                        // Without this, a valid leader (present in the CSV) fails to verify
                                        // and is wrongly marked "unverified".
                                        const alias = normalizeAlias(rawAlias);
                                        if (!alias) return;
                                        setManualLeaderInputs(prev => ({ ...prev, [rt]: alias }));   // show what is checked/submitted
                                        setManualLeaderCheck(prev => ({ ...prev, [rt]: { checking: true } }));
                                        const res = await checkLeaderLevel(alias, reqNum, requesterAlias);
                                        if (res.status === 'self') {
                                            // Requester cannot be their own leader → HARD BLOCK
                                            setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'self' } }));
                                        } else if (res.status === 'ok') {
                                            // Verified at/above required level → accept
                                            setLeadersByRT(prev => ({ ...prev, [rt]: { alias, jobLevel: res.jobLevel, manual: true } }));
                                            setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'ok', jobLevel: res.jobLevel } }));
                                        } else if (res.status === 'not_in_system') {
                                            // CARDS is authoritative and has NO such person → invalid alias → HARD BLOCK.
                                            // (Do NOT set the leader — this used to be a soft warn under the stale CSV.)
                                            setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'not_in_system' } }));
                                        } else if (res.status === 'not_found') {
                                            // CARDS UNAVAILABLE (couldn't verify) → allow, but warn (prior soft behavior).
                                            setLeadersByRT(prev => ({ ...prev, [rt]: { alias, jobLevel: null, manual: true, unverified: true } }));
                                            setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'not_found' } }));
                                        } else if (res.status === 'too_low') {
                                            // Below required level → HARD BLOCK (do not set leader)
                                            setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'too_low', jobLevel: res.jobLevel } }));
                                        } else {
                                            setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'error' } }));
                                        }
                                    };
                                    const setLeaderManually = () => validateAndSetLeader(manualLeaderInputs[rt] || '');
                                    // Chain data for this RT (captured from the lookup). Full chain for display;
                                    // eligible = at/above this RT's required level, for the quick-pick dropdown.
                                    const fullChain = Array.isArray(chainByRT[rt]) ? chainByRT[rt] : [];
                                    const eligibleChain = fullChain.filter(c => c && c.jobLevel && c.jobLevel >= reqNum && c.alias && normalizeAlias(c.alias) !== normalizeAlias(requesterAlias));
                                    return (
                                    <div style={{ marginTop: '8px' }}>
                                        {/* Quick-pick: select an eligible (L{reqNum}+) leader from the requester's
                                            management chain. Selecting one runs the SAME validation as manual entry. */}
                                        {eligibleChain.length > 0 && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#333', marginBottom: '4px' }}>Select from your management chain ({requiredLevel}+)</label>
                                                <select value=""
                                                    onChange={(e) => { const a = e.target.value; if (a) { setManualLeaderInputs(prev => ({ ...prev, [rt]: a })); validateAndSetLeader(a); } }}
                                                    disabled={chk.checking}
                                                    style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', background: '#fff' }}>
                                                    <option value="">-- Select a leader from your chain --</option>
                                                    {eligibleChain.map(c => <option key={c.alias} value={c.alias}>{c.alias} (L{c.jobLevel}){c.title ? ` — ${c.title}` : ''}</option>)}
                                                </select>
                                                <div style={{ textAlign: 'center', fontSize: '11px', color: '#94a3b8', margin: '6px 0' }}>— or enter manually —</div>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input type="text" value={manualLeaderInputs[rt] || ''}
                                                onChange={(e) => { setManualLeaderInputs(prev => ({ ...prev, [rt]: e.target.value })); setManualLeaderCheck(prev => ({ ...prev, [rt]: {} })); }}
                                                onBlur={(e) => { const v = e.target.value; setManualLeaderInputs(prev => ({ ...prev, [rt]: normalizeAlias(v) })); }}
                                                placeholder={`Enter ${requiredLevel} manager alias`}
                                                style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                                            <button type="button" onClick={setLeaderManually} disabled={chk.checking || !(manualLeaderInputs[rt] || '').trim()}
                                                style={{ padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: chk.checking ? 'wait' : 'pointer', border: 'none', background: '#0073bb', color: '#fff' }}>
                                                {chk.checking ? 'Checking...' : 'Set'}
                                            </button>
                                        </div>
                                        {/* Full management chain (read-only), for context — parity with T321. */}
                                        {fullChain.length > 0 && (
                                            <div style={{ marginTop: '8px', fontSize: '11px', color: '#64748b' }}>
                                                Full chain: {fullChain.map(c => `${c.alias}${c.jobLevel ? ` (L${c.jobLevel})` : ''}`).join(' → ')}
                                            </div>
                                        )}
                                        {chk.status === 'self' && (
                                            <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#d13212' }}>
                                                ❌ You cannot select yourself as your approval leader. Please enter a different leader at or above the required level.
                                            </div>
                                        )}
                                        {chk.status === 'too_low' && (
                                            <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#d13212' }}>
                                                ❌ Leader submitted by you is <strong>below the required level</strong>. {rt} requires ≥ {requiredLevel}, but <strong>{manualLeaderInputs[rt]}</strong> is L{chk.jobLevel}. Please enter a valid {requiredLevel}+ leader.
                                            </div>
                                        )}
                                        {chk.status === 'not_in_system' && (
                                            <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#d13212' }}>
                                                ❌ <strong>{manualLeaderInputs[rt]}</strong> is not a valid Amazon employee alias. Please enter a valid {requiredLevel}+ manager.
                                            </div>
                                        )}
                                        {chk.status === 'not_found' && (
                                            <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' }}>
                                                ⚠️ Could not verify <strong>{manualLeaderInputs[rt]}</strong> right now (directory lookup unavailable). You may proceed, but ensure the leader is at or above <strong>{requiredLevel}</strong>, otherwise the request will be rejected.
                                            </div>
                                        )}
                                        {chk.status === 'error' && (
                                            <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#fff3cd', border: '1px solid #ffe69c', fontSize: '12px', color: '#856404' }}>
                                                ⚠️ Could not verify the leader right now. Please try again.
                                            </div>
                                        )}
                                    </div>
                                    );
                                })()}
                            </div>
                        );
                    })}
                </div>

                <button type="button" onClick={() => setStep(57)} disabled={!hasLeaders}
                    style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: hasLeaders ? 'pointer' : 'not-allowed', border: 'none', background: hasLeaders ? '#ff9900' : '#e2e6ea', color: hasLeaders ? '#000' : '#888', marginTop: '16px' }}>
                    {hasLeaders ? 'Continue to User Logins →' : 'Select leaders to continue'}
                </button>
            </PageLayout>
        );
    }

    // ════════════════════════════════════════
    // STEP 57: User Logins
    // ════════════════════════════════════════
    if (step === 57) {
        const hasUsers = Object.values(userListByRT).some(v => v && v.trim()) || (userList && userList.trim());
        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle="User Logins">
                <button type="button" onClick={() => setStep(useCaseType === 'unlisted' ? 56 : 5)}
                    style={{ padding: '10px 18px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: '2px solid #0073bb', background: '#f0f8ff', color: '#0073bb', marginBottom: '16px' }}>
                    ← Back
                </button>

                <div className="section">
                    <div className="section-title">👥 User Logins by Permission Group</div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Enter users for each permission group. Different groups can have different users.</div>
                    {Object.entries(riskGroups).map(([rt, perms], idx) => {
                        const rtPermNames = perms.map(p => p.name).join(', ');
                        const rtUserList = userListByRT[rt] || userList || '';
                        const userCount = normalizeAliasList(rtUserList).length;
                        return (
                            <div key={rt} style={{ marginBottom: '14px', padding: '14px 16px', borderRadius: '8px', border: '1px solid #e2e6ea', background: '#fff' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={getRiskBadgeStyle(rt)}>{rt}</span>
                                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{rtPermNames}</span>
                                        <span style={{ fontSize: '11px', color: '#666' }}>({riskTypeConfig[rt]?.accessDays} days)</span>
                                    </div>
                                    {userCount > 0 && <span style={{ fontSize: '12px', fontWeight: '600', color: '#0073bb' }}>{userCount} user(s)</span>}
                                </div>
                                <textarea
                                    value={rtUserList}
                                    onChange={(e) => setUserListByRT(prev => ({ ...prev, [rt]: e.target.value }))}
                                    onBlur={(e) => { const v = e.target.value; setUserListByRT(prev => ({ ...prev, [rt]: normalizeAliasListText(v) })); }}
                                    placeholder={`Enter logins for ${rtPermNames}...`}
                                    rows={3}
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #dde1e6', fontSize: '14px', resize: 'vertical' }}
                                />
                                <button type="button" onClick={() => handlePasteFromClipboard(rt)}
                                    style={{ marginTop: '6px', fontSize: '12px', color: '#0073bb', background: 'none', border: '1px solid #b8d4f0', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}>
                                    📋 Paste from Excel
                                </button>
                                {idx === 0 && Object.keys(riskGroups).length > 1 && (
                                    <button type="button" onClick={() => {
                                        const firstRT = Object.keys(riskGroups)[0];
                                        const firstList = userListByRT[firstRT] || userList || '';
                                        if (firstList.trim()) {
                                            const updated = {};
                                            Object.keys(riskGroups).forEach(r => { updated[r] = firstList; });
                                            setUserListByRT(updated);
                                        }
                                    }} style={{ display: 'block', width: '100%', padding: '10px 16px', marginTop: '10px', borderRadius: '8px', border: '2px solid #0073bb', background: '#f0f8ff', color: '#0073bb', fontSize: '14px', fontWeight: '700', cursor: 'pointer', textAlign: 'center' }}>
                                        📋 Copy first group's users to all groups
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                <button type="button" onClick={() => setStep(60)} disabled={!hasUsers}
                    style={{ width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px', fontWeight: '700', cursor: hasUsers ? 'pointer' : 'not-allowed', border: 'none', background: hasUsers ? '#ff9900' : '#e2e6ea', color: hasUsers ? '#000' : '#888', marginTop: '16px' }}>
                    {hasUsers ? '🔍 Validate Users & Continue →' : 'Enter user logins to continue'}
                </button>
            </PageLayout>
        );
    }

    // ════════════════════════════════════════
    // STEP 60: Validate & Submit
    // ════════════════════════════════════════
    if (step === 60) {
        const permissionNames = selectedObjects.map(p => p.name);

        // Determine which checks are needed based on selected risk types (for display)
        const requiredChecks = new Set();
        selectedRiskTypes.forEach(rt => {
            (riskTypeConfig[rt]?.checks || []).forEach(c => requiredChecks.add(c));
        });
        const needsBasePI = requiredChecks.has('basePI');
        const needsActivity = requiredChecks.has('inactivity');
        const needsJobLevel = requiredChecks.has('jobLevel');
        const needsSearchActivity = requiredChecks.has('searchActivity');
        const hasOnlyLRP = selectedRiskTypes.every(rt => rt === 'LRP');
        // Only skip validation if requesting ONLY base_pi itself (no Base PI check needed for base_pi)
        const isOnlyBasePI = selectedArray.length === 1 && selectedArray[0] === 'base_pi';
        // Skip Base PI check for LRP if base_pi is included in the same request (Option A)
        const lrpIncludesBasePI = selectedArray.includes('base_pi');

        // Listed (approved use case): the per-user department rule applies on every tier.
        const isListed = useCaseType === 'approved';
        const usersOf = (rt) => normalizeAliasList(userListByRT[rt] || userList || '');
        const rowTiers = (row) => selectedRiskTypes.filter(rt => usersOf(rt).includes(normalizeAlias(row.login)));

        // Evaluate eligibility for a specific risk type (Not Found + department first, then the tier checks)
        const evaluateEligibilityForRiskType = (row, rt) =>
            rowEligibleForRT(row, riskTypeConfig[rt]?.checks || [], rt, lrpIncludesBasePI, isListed, teamLabel);

        // Get reasons why a user is ineligible for a specific risk type
        // eslint-disable-next-line no-unused-vars
        const getReasonsForRiskType = (row, rt) =>
            reasonsForRT(row, riskTypeConfig[rt]?.checks || [], rt, lrpIncludesBasePI, isListed, teamLabel);

        // ── Per-reason bucketing for exception capture ──
        // Inactivity = per-user exception (individual problem).
        // Base PI / Job Level = ONE shared exception per risk type (process problem).
        const isInactiveForRiskType = (row, rt) => {
            const checks = riskTypeConfig[rt]?.checks || [];
            return checks.includes('inactivity') && row.inactivity === 'Inactive';
        };
        const isNoBasePIForRiskType = (row, rt) => {
            const checks = riskTypeConfig[rt]?.checks || [];
            if (rt === 'LRP' && lrpIncludesBasePI) return false; // Option A skip
            return checks.includes('basePI') && row.basePIAccess === 'No';
        };
        const isLowJobLevelForRiskType = (row, rt) => {
            const checks = riskTypeConfig[rt]?.checks || [];
            if (!checks.includes('jobLevel')) return false;
            const level = parseInt(row.jobLevel);
            return !isNaN(level) && level < 3;
        };
        // Users needing a PER-USER inactivity note for this risk type.
        const getInactiveUsersForRiskType = (rt) =>
            getIneligibleForRiskType(rt).filter(u => isInactiveForRiskType(u, rt));
        // Users under the SHARED Base PI note for this risk type.
        const getNoBasePIUsersForRiskType = (rt) =>
            getIneligibleForRiskType(rt).filter(u => isNoBasePIForRiskType(u, rt));
        // Users under the SHARED Job Level note for this risk type.
        const getLowJobLevelUsersForRiskType = (rt) =>
            getIneligibleForRiskType(rt).filter(u => isLowJobLevelForRiskType(u, rt));

        // Overall eligibility for display (based on highest risk type checks). Not Found and the
        // department rule run before the Base-PI-only / Option A short-circuits.
        const evaluateEligibility = (row) => overallEligible(row, {
            isOnlyBasePI, hasOnlyLRP, lrpIncludesBasePI, needsBasePI, needsActivity, needsJobLevel,
            tiers: rowTiers(row), isListed, teamLabel,
        });

        const getEligibilityReasons = (row) => {
            const reasons = [...new Set(rowTiers(row).map(rt => deptReason(row, rt, isListed, teamLabel)).filter(Boolean))];
            if (needsBasePI && row.basePIAccess === 'No') reasons.push('No Base PI');
            if (needsActivity && row.inactivity === 'Inactive') reasons.push(`Inactive (${row.inactiveDays} days)`);
            if (needsJobLevel) {
                const level = parseInt(row.jobLevel);
                if (!isNaN(level) && level < 3) reasons.push(`Job Level ${level} < 3`);
            }
            return reasons;
        };

        // Get ineligible users per risk type (for exception details)
        const getIneligibleForRiskType = (rt) => {
            if (!validationResults || !validationResults.tableData) return [];
            // Only check users that are in this risk type's textarea (case-insensitive)
            const rtUsers = normalizeAliasList(userListByRT[rt] || userList || '');
            return validationResults.tableData.filter(row => 
                rtUsers.includes(normalizeAlias(row.login)) && selectedUsers.includes(row.login) && !evaluateEligibilityForRiskType(row, rt)
            );
        };

        // Check which risk types have ineligible selected users
        const riskTypesWithExceptions = selectedRiskTypes.filter(rt => {
            if (!validationResults || !validationResults.tableData) return false;
            return getIneligibleForRiskType(rt).length > 0;
        });

        // Per-row department value, resolved once: the server's value for this team, else N/A (Not Found /
        // unlisted), else the deptMatch.js fallback for rows from the old validator (information only).
        const resolveDept = (row) => {
            if (row.departmentMatch && row.departmentMatchTeam === teamLabel) return { ...row, departmentMatchSource: 'server' };
            if (String(row.eligible || '').includes('Not Found') || !isListed) return { ...row, departmentMatch: 'N/A', departmentMatchSource: 'legacy' };
            return { ...row, departmentMatch: deptMatch(teamConfig, row.department, row.departmentSource || 'legacy'), departmentMatchSource: 'legacy' };
        };

        // One recompute for the normal and the Base-PI-only paths (no hardcoded FULL anywhere).
        const applyEligibility = (results) => {
            results.tableData = results.tableData.map(resolveDept).map(row => {
                const isEligible = evaluateEligibility(row);
                return {
                    ...row,
                    eligible: row.eligible && row.eligible.includes('Not Found') 
                        ? '⚠️ Not Found' 
                        : (isEligible ? '✅ Eligible' : '❌ Not Eligible'),
                    eligibleBool: isEligible,
                    reasons: getEligibilityReasons(row)
                };
            });
            const eligible = results.tableData.filter(r => r.eligibleBool);
            const ineligible = results.tableData.filter(r => !r.eligibleBool && !r.eligible.includes('Not Found'));
            const notFound = results.tableData.filter(r => r.eligible.includes('Not Found'));
            results.totalEligible = eligible.length;
            results.totalIneligible = ineligible.length;
            results.totalNotFound = notFound.length;
            results.eligibleUsers = eligible.map(r => r.login);
            results.ineligibleUsers = ineligible.map(r => r.login);
            if (results.totalEligible === results.totalUsers) {
                results.recommendation = 'FULL_APPROVAL';
                results.recommendationText = '✅ FULL APPROVAL - All users meet all criteria';
            } else if (results.totalEligible === 0) {
                results.recommendation = 'REJECT';
                results.recommendationText = '❌ REJECT - No users meet criteria';
            } else {
                results.recommendation = 'PARTIAL_APPROVAL';
                results.recommendationText = `⚠️ PARTIAL APPROVAL - ${results.totalEligible}/${results.totalUsers} users eligible`;
            }
            setSelectedUsers(eligible.filter(r => !rowBlocked(r, isListed)).map(r => r.login));
            return results;
        };

        // Users the server would refuse on this listed request (department not on the team's list / unknown).
        const getBlockedUsers = () => ((validationResults && validationResults.tableData) || []).filter(r => rowBlocked(r, isListed));
        const basePIOnlyFlagged = isOnlyBasePI && !!(validationResults && (validationResults.tableData || []).some(r => !r.eligibleBool));

        // Banner actions (§4.5): remove the blocked users, or start an unlisted request for them.
        const removeBlockedUsers = (blocked) => {
            const next = removeBlocked(userList, userListByRT, blocked);
            setUserList(next.userList);
            setUserListByRT(next.userListByRT);
            const gone = new Set(blocked.map(b => normalizeAlias(b.login)));
            setServerDeptBlock(null);
            if (validationResults && validationResults.tableData) {
                const remaining = { ...validationResults, tableData: validationResults.tableData.filter(r => !gone.has(normalizeAlias(r.login))) };
                remaining.totalUsers = remaining.tableData.length;
                setValidationResults(applyEligibility(remaining));
            }
        };
        const startUnlistedForBlocked = (blocked) => {
            const draft = unlistedDraft(blocked, [...selectedPermissions]);
            setUseCaseType(draft.useCaseType);
            setTeamValue('');
            setTeamLabel('');
            setTeamConfig(null);
            setFunctionDropdown([]);
            setOrgName('');
            setUserList(draft.userList);
            setUserListByRT(Object.fromEntries(Object.keys(riskGroups).map(rt => [rt, draft.userList])));
            setValidationResults(null);
            setSelectedUsers([]);
            setServerDeptBlock(null);
            setStep(1);
            window.scrollTo(0, 0);
        };

        const handleValidate = async () => {
            // Bare logins per risk type group (shared alias rule); the textareas re-render with what
            // will be validated and submitted.
            const normByRT = {};
            Object.keys(riskGroups).forEach(rt => { normByRT[rt] = normalizeAliasList(userListByRT[rt] || userList || ''); });
            setUserListByRT(prev => {
                const next = { ...prev };
                Object.keys(normByRT).forEach(rt => { next[rt] = normalizeAliasListText(prev[rt] || userList || ''); });
                return next;
            });
            // Collect all unique users across all risk type groups
            const allUsers = new Set();
            Object.keys(normByRT).forEach(rt => normByRT[rt].forEach(u => allUsers.add(u)));
            if (allUsers.size === 0) { setSubmitError('Please enter user logins for at least one permission group'); return; }
            
            // Check each RT has at least some users
            const emptyRTs = Object.keys(normByRT).filter(rt => normByRT[rt].length === 0);
            if (emptyRTs.length > 0) { setSubmitError(`Please enter users for: ${emptyRTs.join(', ')}`); return; }

            setIsValidating(true);
            setSubmitError('');
            setValidationResults(null);
            setSelectedUsers([]);
            setIneligibleNotes({});
            setSharedExceptions({});
            setValidationProgress({ progress: 0, message: 'Starting validation...' });

            const combinedUserList = [...allUsers].join(',');

            setServerDeptBlock(null);
            try {
                // Every request runs server validation (incl. Base-PI-only): the job ids are the evidence
                // storeResults requires, and the job rows carry the server per-user department result.
                const results = await validateUsersInBatches({
                    userList: combinedUserList,
                    functionDropdown: (() => { const fn = Array.isArray(functionDropdown) ? functionDropdown.join(', ') : functionDropdown; return fn || (teamConfig && teamConfig.functionDropdown) || 'Operations Team'; })(),
                    teamLabel,
                    teamValue,
                    useCase: permissionNames.join(', '),
                    justification: '',
                    priority: priority,
                    requester: requesterAlias
                }, (progressUpdate) => setValidationProgress(progressUpdate));

                if (isOnlyBasePI) {
                    // No tier checks for base_pi only; Not Found and the department rule still apply.
                    const users = [...allUsers];
                    const lrpResults = {
                        totalUsers: users.length,
                        totalFound: users.length,
                        totalEligible: users.length,
                        totalIneligible: 0,
                        totalNotFound: 0,
                        eligibleUsers: users,
                        ineligibleUsers: [],
                        recommendation: 'FULL_APPROVAL',
                        recommendationText: '✅ Base PI — no validation checks required',
                        tableData: mergeBasePIRows(users.map(u => ({ 
                            login: u.trim(), 
                            inactivity: 'N/A',
                            inactiveDays: 0,
                            basePIAccess: 'N/A', 
                            jobFamily: 'N/A',
                            jobLevel: 'N/A',
                            jobLevelStatus: 'N/A',
                            searchActivity: 'NO_DATA',
                            eligible: '✅ Eligible', 
                            eligibleBool: true 
                        })), (results && results.tableData) || []),
                        jobIds: (results && results.jobIds) || [],
                        canProceed: true
                    };
                    applyEligibility(lrpResults);
                    setValidationResults(lrpResults);
                    return;
                }
                
                // Re-evaluate eligibility based on selected risk types
                if (results && results.tableData) {
                    applyEligibility(results);
                }
                setValidationResults(results);
            } catch (err) {
                // Fail closed: no validation evidence means no submit.
                setSubmitError(isOnlyBasePI ? 'Validation failed, please retry. ' + err.message : 'Validation error: ' + err.message);
            } finally {
                setIsValidating(false);
            }
        };

        const handleSubmit = async () => {
            if (!validationResults) { setSubmitError('Please validate users first'); return; }
            if (selectedUsers.length === 0) { setSubmitError('Please select at least one user'); return; }
            const blockedSelected = getBlockedUsers().filter(r => selectedUsers.includes(r.login));
            if (blockedSelected.length > 0) {
                setSubmitError(`${blockedSelected.length} selected user(s) aren't eligible for the ${teamLabel} approved use case (department not on its list). Remove them or start an unlisted request for them.`);
                return;
            }
            if (useCaseType === 'unlisted') {
                const missingLeaders = selectedRiskTypes.filter(rt => !leadersByRT[rt]);
                if (missingLeaders.length > 0) { setSubmitError(`Please set leaders for: ${missingLeaders.join(', ')}`); return; }
                // Requester cannot be their own leader (any path).
                const normAlias = normalizeAlias;
                const me = normAlias(requesterAlias);
                const selfLeaderRTs = selectedRiskTypes.filter(rt => me && normAlias(leadersByRT[rt]?.alias) === me);
                if (selfLeaderRTs.length > 0) { setSubmitError(`You cannot be your own approval leader (${selfLeaderRTs.join(', ')}). Please choose a different leader.`); return; }
                // A manually entered leader must have passed the level check (ok, or not_found when CARDS is unavailable).
                const uncheckedRTs = selectedRiskTypes.filter(rt => {
                    const l = leadersByRT[rt];
                    if (!l || !(l.manual || l.source === 'manual')) return false;
                    const st = (manualLeaderCheck[rt] || {}).status;
                    return st !== 'ok' && st !== 'not_found';
                });
                if (uncheckedRTs.length > 0) { setSubmitError(`Please re-enter and verify the leader for: ${uncheckedRTs.join(', ')}`); return; }
            }
            
            // Check exception notes per risk type: inactivity = per-user; Base PI / Job Level = one shared note.
            for (const rt of riskTypesWithExceptions) {
                const missingInactivity = getInactiveUsersForRiskType(rt).filter(u => !(ineligibleNotes[`${rt}:${u.login}`] || '').trim());
                if (missingInactivity.length > 0) {
                    setSubmitError(`Please provide inactivity exception details for ${riskTypeConfig[rt]?.label || rt}: ${missingInactivity.map(u => u.login).join(', ')}`);
                    return;
                }
                if (getNoBasePIUsersForRiskType(rt).length > 0 && !(sharedExceptions[`${rt}:basePI`] || '').trim()) {
                    setSubmitError(`Please provide the shared Base PI exception for ${riskTypeConfig[rt]?.label || rt}.`);
                    return;
                }
                if (getLowJobLevelUsersForRiskType(rt).length > 0 && !(sharedExceptions[`${rt}:jobLevel`] || '').trim()) {
                    setSubmitError(`Please provide the shared Job Level exception for ${riskTypeConfig[rt]?.label || rt}.`);
                    return;
                }
            }
            
            setIsSubmitting(true);
            setSubmitError('');
            try {
                const { storeResults, submitToApprovals } = await import('../services/api');

                // Filter results to selected users only
                const selectedSet = new Set(selectedUsers);
                const selectedTableData = validationResults.tableData.filter(r => selectedSet.has(r.login));
                const selectedEligible = selectedTableData.filter(r => r.eligibleBool !== false);
                const selectedIneligible = selectedTableData.filter(r => r.eligibleBool === false);

                // Build use case descriptions from team config
                const getUseCaseForPermission = (permId) => {
                    if (teamConfig && teamConfig.permissions) {
                        const configPerm = teamConfig.permissions.find(p => p.id === permId);
                        if (configPerm && configPerm.useCase) return configPerm.useCase;
                    }
                    return null;
                };

                // ════════════════════════════════════════
                // GROUPED APPROVALS: One DynamoDB record + one approval per risk type
                // Each risk type gets its own requestId, storeResults, and submitToApprovals
                // ════════════════════════════════════════
                const approvalResults = [];
                const riskTypeEntries = Object.entries(riskGroups);
                const isRenewalRequest = requestType === 'renewal' && originalRequestId && originalRequestId !== 'skip';

                for (const [rt, perms] of riskTypeEntries) {
                    const rtPermNames = perms.map(p => p.name);
                    const rtPermIds = perms.map(p => p.id);
                    const rtConfig = riskTypeConfig[rt];
                    const rtLabel = rtConfig?.label || rt;
                    const accessDays = rtConfig?.accessDays || 180;

                    // Calculate access end date — use cycle windows for approved, flat days for unlisted
                    const cycleInfo = useCaseType === 'approved' ? getAccessCycleEndDate(rt) : null;
                    const accessEndDate = cycleInfo ? cycleInfo.endDateFormatted : '';
                    const effectiveAccessDays = cycleInfo ? cycleInfo.daysRemaining : accessDays;

                    // Get use cases from team config for this risk type's permissions
                    const rtUseCases = rtPermIds
                        .map(id => getUseCaseForPermission(id))
                        .filter(Boolean);
                    const rtUniqueUseCases = [...new Set(rtUseCases)];
                    const rtUseCaseText = rtUniqueUseCases.length > 0 
                        ? rtUniqueUseCases.join(', ') 
                        : rtPermNames.join(', ');

                    // Get users specific to this risk type
                    const rtAllUsers = normalizeAliasList(userListByRT[rt] || userList || '');
                    // Filter to only users that were selected (validated as eligible or manually included)
                    const rtSelectedUsers = selectedUsers.filter(u => rtAllUsers.includes(normalizeAlias(u)));

                    // Per-risk-type user eligibility
                    const rtEligibleUsers = rtSelectedUsers.filter(login => {
                        const row = validationResults.tableData.find(r => r.login === login);
                        return row ? evaluateEligibilityForRiskType(row, rt) : true;
                    });
                    const rtIneligibleUsers = rtSelectedUsers.filter(login => {
                        const row = validationResults.tableData.find(r => r.login === login);
                        return row ? !evaluateEligibilityForRiskType(row, rt) : false;
                    });

                    // Exception notes for this risk type:
                    //   - Inactivity → per-user note (individual problem)
                    //   - Base PI / Job Level → ONE shared note each (process problem)
                    const rtExceptionNotes = {}; // per-user INACTIVITY notes only
                    getInactiveUsersForRiskType(rt).forEach(u => {
                        if (rtSelectedUsers.includes(u.login)) {
                            const note = ineligibleNotes[`${rt}:${u.login}`];
                            if (note) rtExceptionNotes[u.login] = note;
                        }
                    });
                    const rtBasePIUsers = getNoBasePIUsersForRiskType(rt).map(u => u.login).filter(l => rtSelectedUsers.includes(l));
                    const rtJobLevelUsers = getLowJobLevelUsersForRiskType(rt).map(u => u.login).filter(l => rtSelectedUsers.includes(l));
                    const rtBasePIException = rtBasePIUsers.length > 0 ? (sharedExceptions[`${rt}:basePI`] || '') : '';
                    const rtJobLevelException = rtJobLevelUsers.length > 0 ? (sharedExceptions[`${rt}:jobLevel`] || '') : '';

                    try {
                        // Step 1: Store this risk type's data as its own DynamoDB record
                        const rtDataToStore = {
                            functionDropdown: (() => {
                                const fn = Array.isArray(functionDropdown) ? functionDropdown.join(', ') : functionDropdown;
                                if (fn) return fn;
                                // Fallback: use team config's function for listed use cases
                                if (teamConfig && teamConfig.functionDropdown) return teamConfig.functionDropdown;
                                return 'Operations Team';
                            })(),
                            team: teamLabel,
                            teamValue,
                            useCase: useCaseByRT[rt] || rtPermNames.join(', '),
                            useCaseDescription: useCaseByRT[rt] || '',
                            useCaseValue: rtPermIds.join(','),
                            justification: useCaseByRT[rt] || '',
                            priority: priority,
                            highPriorityJustification: priority === 'High' ? highPriorityJustification : '',
                            bypassJustification: bypassJustification || '',
                            userDepartment: userDepartment || '',
                            userJobFamily: userJobFamily || '',
                            userList: rtSelectedUsers.join(','),
                            requestType: `permission_${requestType}_${useCaseType}`,
                            orgName,
                            requesterAlias: normalizeAlias(requesterAlias),
                            selectedPermissions: rtPermIds,
                            selectedPermissionNames: rtPermNames,
                            permissionLinks: perms.flatMap(p => {
                                if (p.permissionLinks && p.permissionLinks.length > 0) {
                                    return p.permissionLinks;
                                }
                                return [{ name: p.name, link: p.permissionLink || '' }];
                            }),
                            highestApprovalLevel: highestLevel,
                            riskTypes: [rt],
                            riskTypeGroup: rt,
                            riskTypeLabel: rtLabel,
                            groupAccessDays: effectiveAccessDays,
                            accessEndDate: accessEndDate,
                            accessCycleLabel: cycleInfo ? cycleInfo.windowLabel : '',
                            totalUsers: rtSelectedUsers.length,
                            totalEligible: rtEligibleUsers.length,
                            totalIneligible: rtIneligibleUsers.length,
                            totalNotFound: 0,
                            eligibleUsers: rtEligibleUsers,
                            ineligibleUsers: rtIneligibleUsers,
                            ineligibleNotes: Object.keys(rtExceptionNotes).length > 0 ? rtExceptionNotes : {}, // per-user inactivity only
                            basePIException: rtBasePIException,        // shared Base PI note (process-level)
                            basePIUsers: rtBasePIUsers,                // logins the shared Base PI note covers
                            jobLevelException: rtJobLevelException,    // shared Job Level note (process-level)
                            jobLevelUsers: rtJobLevelUsers,            // logins the shared Job Level note covers
                            recommendation: rtIneligibleUsers.length === 0 ? 'FULL_APPROVAL' : 'PARTIAL_APPROVAL',
                            recommendationText: rtIneligibleUsers.length === 0 
                                ? '✅ FULL APPROVAL - All users meet criteria' 
                                : `⚠️ PARTIAL - ${rtEligibleUsers.length}/${rtSelectedUsers.length} eligible`,
                            tableData: selectedTableData,
                            ...(isRenewalRequest ? {
                                previousRequestId: originalRequestId,
                                previousApprovalId: previousApprovalId,
                                previousViewerUrl: `https://permissions-access-management.tse.amazon.dev/viewer?id=${originalRequestId}`,
                            } : {}),
                            l8ManagerAlias: leadersByRT[rt]?.alias || '',
                            // Server-side evidence: the validation jobs this table came from.
                            validationJobIds: validationResults.jobIds || [],
                        };

                        const storeResult = await storeResults(rtDataToStore);
                        if (storeResult.error) {
                            // Department hard block: show the banner with the server's users + the two actions.
                            if ((storeResult.code === 'DEPARTMENT_NOT_ELIGIBLE' || storeResult.code === 'DEPARTMENT_UNKNOWN')
                                && Array.isArray(storeResult.users)) {
                                setServerDeptBlock({ code: storeResult.code, team: storeResult.team || teamLabel, users: storeResult.users });
                            }
                            throw new Error(storeResult.error);
                        }
                        const rtRequestId = storeResult.requestId;
                        if (!rtRequestId) throw new Error('Failed to get request ID');

                        const rtViewerUrl = `https://permissions-access-management.tse.amazon.dev/viewer?id=${rtRequestId}`;

                        // Step 2: Create approval on approvals.amazon.com for this risk type
                        const approvalTitle = `T178 ${rt} Permission Request${isRenewalRequest ? ' (Renewal)' : ''} — ${teamLabel} — ${rtPermNames.join(', ')}`;
                        
                        // Build permission links
                        const rtPermLinks = perms.map(p => `${p.name}: ${p.permissionLink || 'N/A'}`);
                        
                        const approvalSummary = [
                            `1. Risk Type: ${rtLabel} (${cycleInfo ? `Cycle: ${cycleInfo.windowLabel}, expires ${accessEndDate}` : `${accessDays} days access`})`,
                            `2. Team: ${teamLabel} (${functionDropdown} / ${orgName})`,
                            `3. Permissions: ${rtPermNames.join(', ')}`,
                            `4. Use Case / Justification: ${useCaseByRT[rt] || 'N/A'}`,
                            `5. Permission Links:`,
                            ...rtPermLinks.map(link => `   - ${link}`),
                            `6. Users (${rtSelectedUsers.length}): ${rtSelectedUsers.join(', ')}`,
                            rtIneligibleUsers.length > 0 ? `7. Exceptions (${rtIneligibleUsers.length}): ${rtIneligibleUsers.join(', ')}` : '',
                            `${rtIneligibleUsers.length > 0 ? '8' : '7'}. Request Type: ${useCaseType === 'approved' ? 'Pre-Approved' : 'Unlisted'} — ${requestType === 'new' ? 'New Request' : 'Renewal'}`,
                            `${rtIneligibleUsers.length > 0 ? '9' : '8'}. Requester: ${requesterAlias}`,
                            `${rtIneligibleUsers.length > 0 ? '10' : '9'}. View Details: ${rtViewerUrl}`,
                        ].filter(Boolean).join('\r\n');

                        const result = await submitToApprovals({
                            requestId: rtRequestId,
                            requesterAlias,
                            templateId: useCaseType === 'unlisted' 
                                ? ({ LRP: '225065', MRP: '225064', HRP: '225066', CRP: '225068' }[rt] || '225065')
                                : '221400',
                            l8ManagerAlias: leadersByRT[rt]?.alias || '',
                            title: approvalTitle,
                            summary: approvalSummary,
                            viewerUrl: rtViewerUrl,
                            formData: rtDataToStore,
                            validationSummary: { 
                                totalUsers: rtSelectedUsers.length, 
                                totalEligible: rtEligibleUsers.length, 
                                totalIneligible: rtIneligibleUsers.length, 
                                totalNotFound: 0, 
                                recommendation: rtIneligibleUsers.length === 0 ? 'FULL_APPROVAL' : 'PARTIAL_APPROVAL'
                            }
                        });

                        approvalResults.push({
                            riskType: rt,
                            riskTypeLabel: rtLabel,
                            permissions: rtPermNames,
                            requestId: rtRequestId,
                            approvalId: result.approvalId || '',
                            approvalsUrl: result.approvalsUrl || `https://approvals.amazon.com/approval/${result.approvalId}/details/memo-view/notes-and-summary`,
                            viewerUrl: rtViewerUrl,
                            // Server-computed recommendation (storeResults recomputes it from its own validation rows).
                            recommendation: storeResult.recommendation || '',
                            recommendationText: storeResult.recommendationText || '',
                            success: true
                        });
                    } catch (err) {
                        approvalResults.push({
                            riskType: rt,
                            riskTypeLabel: rtLabel,
                            permissions: rtPermNames,
                            error: err.message,
                            success: false
                        });
                    }
                }

                // Check if any approvals failed
                const failedApprovals = approvalResults.filter(r => !r.success);
                if (failedApprovals.length === riskTypeEntries.length) {
                    throw new Error('All approvals failed: ' + failedApprovals.map(f => `${f.riskType}: ${f.error}`).join('; '));
                }

                clearSavedFormState();
                setSubmitSuccess({
                    requestId: approvalResults.find(r => r.success)?.requestId || '',
                    viewerUrl: approvalResults.find(r => r.success)?.viewerUrl || '',
                    approvalResults,
                    totalApprovals: riskTypeEntries.length,
                    successCount: approvalResults.filter(r => r.success).length,
                    failedCount: failedApprovals.length,
                });
            } catch (err) {
                setSubmitError('Error: ' + err.message);
            } finally {
                setIsSubmitting(false);
            }
        };

        // Success view
        if (submitSuccess) {
            return (
                <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} title="🔐 T178 Permission Request" subtitle="Request Submitted" showStepper={false}>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
                            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>Request Submitted Successfully!</h2>
                            <p style={{ fontSize: '14px', color: '#555' }}>
                                {submitSuccess.successCount} of {submitSuccess.totalApprovals} approval(s) created for {teamLabel}
                            </p>
                        </div>

                        {/* Grouped approval results */}
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '10px' }}>📋 Approvals by Risk Type:</div>
                            {submitSuccess.approvalResults.map((ar, idx) => (
                                <div key={idx} style={{
                                    padding: '14px 16px', borderRadius: '10px', marginBottom: '10px',
                                    border: `1px solid ${ar.success ? '#bbf7d0' : '#ffc1c8'}`,
                                    background: ar.success ? '#f0fdf4' : '#ffeef0'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                        <span style={getRiskBadgeStyle(ar.riskType)}>{ar.riskType}</span>
                                        <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                                            {ar.permissions.join(', ')}
                                        </span>
                                    </div>
                                    {ar.success ? (
                                        <div>
                                            <div style={{ fontSize: '12px', color: '#555', marginBottom: '4px' }}>
                                                Request ID: <strong>{ar.requestId}</strong>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#555', marginBottom: '6px' }}>
                                                Approval ID: <strong>{ar.approvalId}</strong>
                                            </div>
                                            {ar.recommendationText && (
                                                <div style={{ fontSize: '12px', color: '#555', marginBottom: '6px' }}>
                                                    Recommendation (server check): <strong>{ar.recommendationText}</strong>
                                                </div>
                                            )}
                                            <a href={ar.approvalsUrl} target="_blank" rel="noopener noreferrer"
                                                style={{ fontSize: '13px', color: '#0073bb', textDecoration: 'underline', fontWeight: '600' }}>
                                                View on Approvals.amazon.com ↗
                                            </a>
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: '13px', color: '#d13212' }}>
                                            ❌ Failed: {ar.error}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'grid', gap: '10px' }}>
                            <a href={submitSuccess.viewerUrl} target="_blank" rel="noopener noreferrer"
                                style={{ display: 'block', padding: '14px', background: '#0073bb', color: '#fff', borderRadius: '8px', textDecoration: 'none', textAlign: 'center', fontWeight: '600', fontSize: '14px' }}>
                                📊 View Request Details
                            </a>
                            <a href="/my-requests"
                                style={{ display: 'block', padding: '14px', background: '#f0f8ff', color: '#0073bb', borderRadius: '8px', textDecoration: 'none', textAlign: 'center', fontWeight: '600', fontSize: '14px', border: '1px solid #b8d4f0' }}>
                                📋 Track on My Requests
                            </a>
                            <button type="button" onClick={() => { setStep(1); setSelectedPermissions(new Set()); setSubmitSuccess(null); }}
                                style={{ padding: '14px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: '1px solid #ddd', background: '#fff', color: '#333' }}>
                                📝 Submit Another Request
                            </button>
                        </div>

                        {/* Expected timeline */}
                        <div style={{ marginTop: '16px', padding: '12px 16px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea', fontSize: '13px', color: '#555' }}>
                            <div style={{ fontWeight: '600', color: '#333', marginBottom: '6px' }}>⏱️ What happens next?</div>
                            <div>• LRP/MRP requests: typically approved within <strong>1-2 business days</strong></div>
                            <div>• HRP requests: typically <strong>2-3 business days</strong> (3-stage: SPS Team → L7 → Access Management)</div>
                            <div>• CRP requests: typically <strong>3-5 business days</strong> (3-stage: SPS Team → L8 → Access Management)</div>
                            <div style={{ marginTop: '6px', color: '#666' }}>You'll receive a notification when your request is actioned.</div>
                        </div>
            </PageLayout>
            );
        }

        // Preview + Submit form
        return (
            <PageLayout step={step} useCaseType={useCaseType} teamLabel={teamLabel} isValidated={!!validationResults} title="🔐 T178 Permission Request" subtitle="Validate & Submit">
                    {/* One-time reassurance modal — fires when validation returns flagged users */}
                    {showFlaggedInfoModal && validationResults && (
                        <div
                            onClick={() => setShowFlaggedInfoModal(false)}
                            style={{ position: 'fixed', inset: 0, background: 'rgba(20,30,45,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
                        >
                            <div
                                onClick={(e) => e.stopPropagation()}
                                role="dialog"
                                aria-modal="true"
                                style={{ background: '#fff', borderRadius: '14px', maxWidth: '520px', width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}
                            >
                                <div style={{ background: '#0073bb', color: '#fff', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '26px' }}>ℹ️</span>
                                    <span style={{ fontSize: '18px', fontWeight: '800' }}>Please read</span>
                                </div>
                                <div style={{ padding: '22px 24px', fontSize: '14px', color: '#12324d', lineHeight: 1.6 }}>
                                    <p style={{ marginTop: 0, marginBottom: '14px' }}>
                                        Some users in your request were flagged. This is often normal and usually does <strong>not</strong> block your request.
                                    </p>
                                    {validationResults.totalNotFound > 0 && (
                                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fffbeb', border: '1px solid #fde68a', marginBottom: '10px' }}>
                                            <strong>⚠️ {validationResults.totalNotFound} user{validationResults.totalNotFound !== 1 ? 's' : ''} "Not Found"</strong> — this can happen due to a temporary data sync delay in our portal. It does not always mean the user is invalid. You can still select those users and submit; our team will verify during review.
                                        </div>
                                    )}
                                    {validationResults.totalIneligible > 0 && (
                                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#ffeef0', border: '1px solid #ffc1c8' }}>
                                            <strong>❌ {validationResults.totalIneligible} user{validationResults.totalIneligible !== 1 ? 's' : ''} "Not Eligible"</strong> — flagged because one eligibility check isn't met in our current data. If you believe this is incorrect, you can still select the user and submit; you'll be asked to add a short justification and our team will review.
                                        </div>
                                    )}
                                </div>
                                <div style={{ padding: '0 24px 22px', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        type="button"
                                        onClick={() => setShowFlaggedInfoModal(false)}
                                        style={{ padding: '11px 22px', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', border: 'none', background: '#0073bb', color: '#fff' }}
                                    >
                                        I understand, continue
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <button type="button" onClick={() => setStep(57)}
                        style={{ padding: '10px 18px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', border: '2px solid #0073bb', background: '#f0f8ff', color: '#0073bb', marginBottom: '16px' }}>
                        ← Back to Users
                    </button>

                    {/* Summary */}
                    <div className="section">
                        <div className="section-title">📋 Request Summary</div>
                        
                        {/* Renewal banner */}
                        {requestType === 'renewal' && originalRequestId && originalRequestId !== 'skip' && (
                            <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#eef6ff', border: '1px solid #b8d4f0', marginBottom: '12px', fontSize: '13px', color: '#0073bb' }}>
                                🔄 <strong>Renewal</strong> of request: {originalRequestId}
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                            <div><span style={{ fontSize: '12px', color: '#666' }}>Requester</span><div style={{ fontWeight: '600' }}>👤 {requesterAlias}{requesterJobLevel ? ` (L${requesterJobLevel})` : ''}</div></div>
                            <div><span style={{ fontSize: '12px', color: '#666' }}>Team</span><div style={{ fontWeight: '600' }}>🏢 {teamLabel}</div></div>
                            <div><span style={{ fontSize: '12px', color: '#666' }}>Function / Org</span><div style={{ fontWeight: '600' }}>{Array.isArray(functionDropdown) ? functionDropdown.join(', ') : functionDropdown} / {orgName}</div></div>
                            <div><span style={{ fontSize: '12px', color: '#666' }}>Request Type</span><div style={{ fontWeight: '600' }}>{useCaseType === 'approved' ? '✅ Pre-Approved' : '📋 Unlisted'} — {requestType === 'new' ? 'New' : 'Renewal'}</div></div>
                        </div>

                        {/* Selected permissions by risk type */}
                        <div style={{ marginBottom: '16px' }}>
                            <span style={{ fontSize: '12px', color: '#666' }}>Permissions ({selectedPermissions.size})</span>
                            {Object.entries(riskGroups).map(([rt, perms]) => {
                                const cycleInfo = useCaseType === 'approved' ? getAccessCycleEndDate(rt) : null;
                                return (
                                <div key={rt} style={{ marginTop: '8px' }}>
                                    <span style={getRiskBadgeStyle(rt)}>{rt} — {cycleInfo ? `until ${cycleInfo.endDateFormatted}` : `${riskTypeConfig[rt].accessDays} days`}</span>
                                    <div style={{ marginTop: '4px', paddingLeft: '12px' }}>
                                        {perms.map(p => <div key={p.id} style={{ fontSize: '13px', padding: '2px 0' }}>• {p.name}</div>)}
                                    </div>
                                    {cycleInfo && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', background: cycleInfo.isGracePeriod ? '#fffbeb' : '#f0fdf4', border: `1px solid ${cycleInfo.isGracePeriod ? '#fde68a' : '#bbf7d0'}`, color: cycleInfo.isGracePeriod ? '#92400e' : '#166534' }}>
                                            {cycleInfo.isGracePeriod ? '⚡' : '📅'} {cycleInfo.message}
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Leader Lookup — per risk type for Unlisted Use Cases */}
                    {useCaseType === 'unlisted' && (
                    <div className="section" style={{ borderLeft: '4px solid #f59e0b', paddingLeft: '16px' }}>
                        <div className="section-title">👔 Leadership Approval (per Risk Type)</div>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                            Each risk type routes to the appropriate leader: CRP→L8, HRP→L7, LRP/MRP→L6.
                        </div>
                        <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#fef2f2', border: '1px solid #fecaca', marginBottom: '14px', fontSize: '13px', color: '#991b1b', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <span style={{ fontSize: '16px', lineHeight: '1' }}>⚠️</span>
                            <span><strong>Important:</strong> Please ensure you select the correct leader for approval. If the wrong leader is selected, your request will be rejected.</span>
                        </div>

                        {/* Auto-lookup all button */}
                        {Object.keys(leadersByRT).length < selectedRiskTypes.length && (
                            <button type="button" onClick={async () => {
                                setIsLookingUpLeader(true);
                                setLeaderLookupError('');
                                try {
                                    // Find the highest level needed — we look up once and can reuse for lower levels
                                    const highestNumeric = highestLevel === 'L8' ? 8 : highestLevel === 'L7' ? 7 : 6;
                                    const result = await lookupManager(requesterAlias, 6); // get full chain from L6+
                                    
                                    if (result.success && result.managerChain) {
                                        const newLeaders = { ...leadersByRT };
                                        const chain = result.managerChain;
                                        
                                        // For each risk type, find the appropriate level manager from the chain
                                        for (const rt of selectedRiskTypes) {
                                            if (newLeaders[rt]) continue; // already set
                                            const requiredLevel = rt === 'CRP' ? 8 : rt === 'HRP' ? 7 : 6;
                                            // Find first manager in chain at or above required level
                                            const match = chain.find(m => m.jobLevel && m.jobLevel >= requiredLevel);
                                            if (match) {
                                                newLeaders[rt] = { alias: match.alias, jobLevel: match.jobLevel, title: match.title || '', source: match.source };
                                            }
                                        }
                                        setLeadersByRT(newLeaders);
                                        
                                        // Check if any RT still missing
                                        const missing = selectedRiskTypes.filter(rt => !newLeaders[rt]);
                                        if (missing.length > 0) {
                                            setLeaderLookupError(`Could not find leaders for: ${missing.join(', ')}. Enter manually.`);
                                            const manualFlags = { ...manualLeaderRT };
                                            missing.forEach(rt => { manualFlags[rt] = true; });
                                            setManualLeaderRT(manualFlags);
                                        }
                                    } else {
                                        setLeaderLookupError(result.message || 'Could not auto-detect leaders. Enter manually.');
                                        const manualFlags = {};
                                        selectedRiskTypes.forEach(rt => { manualFlags[rt] = true; });
                                        setManualLeaderRT(manualFlags);
                                    }
                                } catch (err) {
                                    setLeaderLookupError('Lookup failed: ' + err.message);
                                    const manualFlags = {};
                                    selectedRiskTypes.forEach(rt => { manualFlags[rt] = true; });
                                    setManualLeaderRT(manualFlags);
                                } finally {
                                    setIsLookingUpLeader(false);
                                }
                            }} disabled={isLookingUpLeader}
                                style={{ padding: '10px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: isLookingUpLeader ? 'wait' : 'pointer', border: 'none', background: '#f59e0b', color: '#000', width: '100%', marginBottom: '12px' }}>
                                {isLookingUpLeader ? '⏳ Looking up leaders...' : '🔍 Auto-detect Leaders for All Risk Types'}
                            </button>
                        )}

                        {leaderLookupError && <div style={{ padding: '8px 12px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e', marginBottom: '12px' }}>⚠️ {leaderLookupError}</div>}

                        {/* Per-risk-type leader display */}
                        {selectedRiskTypes.map(rt => {
                            const requiredLevel = rt === 'CRP' ? 'L8' : rt === 'HRP' ? 'L7' : 'L6';
                            const leader = leadersByRT[rt];
                            const isManual = manualLeaderRT[rt];
                            const manualInput = manualLeaderInputs[rt] || '';
                            // Same required level step 56 uses (6/7/8 from requiredLevel).
                            const reqNum = parseInt(requiredLevel.replace('L', ''), 10);
                            const chk = manualLeaderCheck[rt] || {};
                            // Manual "Set" goes through the same self / level / existence checks as step 56.
                            const setLeaderFinalPage = async () => {
                                const alias = normalizeAlias(manualInput);
                                if (!alias) return;
                                setManualLeaderInputs(prev => ({ ...prev, [rt]: alias }));   // show what is checked/submitted
                                setManualLeaderCheck(prev => ({ ...prev, [rt]: { checking: true } }));
                                const res = await checkLeaderLevel(alias, reqNum, requesterAlias);
                                if (res.status === 'self') {
                                    setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'self' } }));
                                } else if (res.status === 'ok') {
                                    setLeadersByRT(prev => ({ ...prev, [rt]: { alias, jobLevel: res.jobLevel, manual: true } }));
                                    setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'ok', jobLevel: res.jobLevel } }));
                                } else if (res.status === 'not_in_system') {
                                    setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'not_in_system' } }));
                                } else if (res.status === 'not_found') {
                                    // CARDS unavailable → allow, but warn (same soft behavior as step 56).
                                    setLeadersByRT(prev => ({ ...prev, [rt]: { alias, jobLevel: null, manual: true, unverified: true } }));
                                    setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'not_found' } }));
                                } else if (res.status === 'too_low') {
                                    setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'too_low', jobLevel: res.jobLevel } }));
                                } else {
                                    setManualLeaderCheck(prev => ({ ...prev, [rt]: { status: 'error' } }));
                                }
                            };

                            return (
                                <div key={rt} style={{ marginBottom: '10px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e2e6ea', background: leader ? '#f0fdf4' : '#fff' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: leader ? '0' : '8px' }}>
                                        <span style={getRiskBadgeStyle(rt)}>{rt}</span>
                                        <span style={{ fontSize: '13px', color: '#666' }}>→ {requiredLevel} required</span>
                                        {leader && (
                                            <>
                                                <span style={{ fontSize: '13px', fontWeight: '600', color: leader.unverified ? '#92400e' : '#166534', marginLeft: 'auto' }}>
                                                    {leader.unverified ? `⚠️ ${leader.alias} (unverified)` : `✅ ${leader.alias} (L${leader.jobLevel})`}
                                                </span>
                                                <button type="button" onClick={() => { 
                                                    const updated = { ...leadersByRT }; delete updated[rt]; setLeadersByRT(updated);
                                                    setManualLeaderRT(prev => ({ ...prev, [rt]: true }));
                                                    setManualLeaderInputs(prev => ({ ...prev, [rt]: '' }));
                                                    setManualLeaderCheck(prev => ({ ...prev, [rt]: {} }));
                                                }} style={{ fontSize: '11px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                                            </>
                                        )}
                                    </div>
                                    {!leader && isManual && (
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input type="text" value={manualInput} onChange={(e) => { setManualLeaderInputs(prev => ({ ...prev, [rt]: e.target.value.toLowerCase().trim() })); setManualLeaderCheck(prev => ({ ...prev, [rt]: {} })); }}
                                                onBlur={(e) => { const v = e.target.value; setManualLeaderInputs(prev => ({ ...prev, [rt]: normalizeAlias(v) })); }}
                                                placeholder={`Enter ${requiredLevel}+ manager alias`}
                                                style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px' }} />
                                            <button type="button" onClick={setLeaderFinalPage} disabled={chk.checking || !manualInput.trim()}
                                                style={{ padding: '8px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: chk.checking ? 'wait' : 'pointer', border: 'none', background: '#ff9900', color: '#000' }}>
                                                {chk.checking ? 'Checking...' : 'Set'}
                                            </button>
                                        </div>
                                    )}
                                    {!leader && isManual && chk.status === 'self' && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#d13212' }}>
                                            ❌ You cannot select yourself as your approval leader. Please enter a different leader at or above the required level.
                                        </div>
                                    )}
                                    {!leader && isManual && chk.status === 'too_low' && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#d13212' }}>
                                            ❌ Leader submitted by you is <strong>below the required level</strong>. {rt} requires ≥ {requiredLevel}, but <strong>{manualInput}</strong> is L{chk.jobLevel}. Please enter a valid {requiredLevel}+ leader.
                                        </div>
                                    )}
                                    {!leader && isManual && chk.status === 'not_in_system' && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', fontSize: '12px', color: '#d13212' }}>
                                            ❌ <strong>{manualInput}</strong> is not a valid Amazon employee alias. Please enter a valid {requiredLevel}+ manager.
                                        </div>
                                    )}
                                    {leader && leader.unverified && chk.status === 'not_found' && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', color: '#92400e' }}>
                                            ⚠️ Could not verify <strong>{leader.alias}</strong> right now (directory lookup unavailable). You may proceed, but ensure the leader is at or above <strong>{requiredLevel}</strong>, otherwise the request will be rejected.
                                        </div>
                                    )}
                                    {!leader && isManual && chk.status === 'error' && (
                                        <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#fff3cd', border: '1px solid #ffe69c', fontSize: '12px', color: '#856404' }}>
                                            ⚠️ Could not verify the leader right now. Please try again.
                                        </div>
                                    )}
                                    {!leader && !isManual && (
                                        <button type="button" onClick={() => setManualLeaderRT(prev => ({ ...prev, [rt]: true }))}
                                            style={{ fontSize: '13px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', marginTop: '6px', fontWeight: '500' }}>
                                            or enter manager manually
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    )}

                    {/* User input — per risk type */}
                    <div className="section">
                        <div className="section-title">👥 User Logins by Permission Group</div>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>Enter users for each permission group. Different groups can have different users.</div>
                        {Object.entries(riskGroups).map(([rt, perms], idx) => {
                            const rtPermNames = perms.map(p => p.name).join(', ');
                            const rtUserList = userListByRT[rt] || userList || '';
                            const userCount = normalizeAliasList(rtUserList).length;
                            return (
                                <React.Fragment key={rt}>
                                <div style={{ marginBottom: '14px', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e6ea', background: '#f8f9fa' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <span style={getRiskBadgeStyle(rt)}>{rt}</span>
                                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>{rtPermNames}</span>
                                        <span style={{ fontSize: '12px', color: '#666' }}>({riskTypeConfig[rt]?.accessDays} days)</span>
                                        {userCount > 0 && <span style={{ fontSize: '11px', fontWeight: '600', color: '#0073bb', marginLeft: 'auto' }}>{userCount} user(s)</span>}
                                    </div>
                                    <textarea 
                                        value={rtUserList}
                                        onChange={(e) => setUserListByRT(prev => ({ ...prev, [rt]: e.target.value }))}
                                        onBlur={(e) => { const v = e.target.value; setUserListByRT(prev => ({ ...prev, [rt]: normalizeAliasListText(v) })); }}
                                        placeholder={`Enter logins for ${rtPermNames}...`}
                                        rows={3}
                                        style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }} />
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '6px' }}>
                                        <button type="button" onClick={() => handlePasteFromClipboard(rt)}
                                            style={{ fontSize: '11px', color: '#0073bb', background: 'none', border: '1px solid #b8d4f0', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer' }}>
                                            📋 Paste from Excel
                                        </button>
                                        {(() => {
                                            const invalid = getInvalidAliases(rtUserList);
                                            if (invalid.length === 0 || !rtUserList.trim()) return null;
                                            return <span style={{ fontSize: '11px', color: '#d13212' }}>⚠️ Invalid: {invalid.slice(0, 3).join(', ')}{invalid.length > 3 ? ` (+${invalid.length - 3})` : ''}</span>;
                                        })()}
                                    </div>
                                </div>
                                {/* Copy to all — show after first group */}
                                {idx === 0 && Object.keys(riskGroups).length > 1 && (
                                    <button type="button" onClick={() => {
                                        const firstRT = Object.keys(riskGroups)[0];
                                        const firstList = userListByRT[firstRT] || userList || '';
                                        if (firstList.trim()) {
                                            const updated = {};
                                            Object.keys(riskGroups).forEach(r => { updated[r] = firstList; });
                                            setUserListByRT(updated);
                                        }
                                    }} style={{ display: 'block', width: '100%', padding: '10px 16px', marginBottom: '14px', borderRadius: '8px', border: '2px solid #0073bb', background: '#f0f8ff', color: '#0073bb', fontSize: '14px', fontWeight: '700', cursor: 'pointer', textAlign: 'center' }}>
                                        📋 Copy first group's users to all groups
                                    </button>
                                )}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {/* Risk-type checks info */}
                    {!validationResults && !isValidating && selectedRiskTypes.length > 0 && (
                        <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea', marginBottom: '16px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>🔍 Validation checks for selected permissions:</div>
                            {selectedRiskTypes.map(rt => (
                                <div key={rt} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={getRiskBadgeStyle(rt)}>{rt}</span>
                                    <span style={{ fontSize: '12px', color: '#555' }}>
                                        {riskTypeConfig[rt].checks.length === 0 
                                            ? '— No checks required' 
                                            : `— ${riskTypeConfig[rt].checks.map(c => {
                                                if (c === 'basePI') return 'Base PI Access';
                                                if (c === 'inactivity') return 'Activity (≤30 days)';
                                                if (c === 'jobLevel') return 'Job Level (≥3)';
                                                if (c === 'searchActivity') return 'Search Activity';
                                                return c;
                                            }).join(', ')}`
                                        }
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Auto-trigger validation on step entry */}
                    {!validationResults && !isValidating && (() => {
                        // Auto-validate immediately
                        setTimeout(() => handleValidate(), 0);
                        return (
                        <div style={{ width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px',
                                fontWeight: '600', border: '1px solid #0073bb', background: '#f0f8ff',
                                color: '#0073bb', marginBottom: '16px', textAlign: 'center' }}>
                            ⏳ Starting validation...
                        </div>
                        );
                    })()}

                    {/* Validation progress */}
                    {isValidating && (
                        <div style={{ padding: '12px', borderRadius: '6px', background: '#f0f8ff', border: '1px solid #b8d4f0', marginBottom: '16px', fontSize: '13px', color: '#0073bb' }}>
                            ⏳ {validationProgress.message || 'Validating...'}
                            {validationProgress.progress > 0 && (
                                <div style={{ marginTop: '8px', height: '4px', borderRadius: '2px', background: '#e2e6ea', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${validationProgress.progress}%`, background: '#0073bb', borderRadius: '2px', transition: 'width 0.3s ease' }} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Validation results — risk-type-aware display */}
                    {validationResults && (
                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                                <div className="section-title" style={{ margin: 0 }}>📊 Validation Results</div>
                                <button type="button" onClick={() => { setValidationResults(null); setSelectedUsers([]); setIneligibleNotes({}); }}
                                    style={{ fontSize: '12px', color: '#0073bb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                    🔄 Re-validate
                                </button>
                            </div>

                            {/* Stats + Recommendation */}
                            <div style={{ padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e2e6ea', marginBottom: '12px' }}>
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontSize: '20px', fontWeight: '700', color: '#232f3e' }}>{validationResults.totalUsers}</span>
                                        <span style={{ fontSize: '13px', color: '#666' }}>Total</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontSize: '20px', fontWeight: '700', color: '#1a7f37' }}>{validationResults.totalEligible}</span>
                                        <span style={{ fontSize: '13px', color: '#1a7f37' }}>Eligible</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontSize: '20px', fontWeight: '700', color: '#d13212' }}>{validationResults.totalIneligible}</span>
                                        <span style={{ fontSize: '13px', color: '#d13212' }}>Ineligible</span>
                                    </div>
                                    {validationResults.totalNotFound > 0 && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span style={{ fontSize: '20px', fontWeight: '700', color: '#d4880f' }}>{validationResults.totalNotFound}</span>
                                            <span style={{ fontSize: '13px', color: '#d4880f' }}>Not Found</span>
                                        </div>
                                    )}
                                </div>
                                <div style={{
                                    padding: '10px 14px', borderRadius: '8px',
                                    backgroundColor: validationResults.recommendation === 'FULL_APPROVAL' ? '#f0fdf4' : validationResults.recommendation === 'REJECT' ? '#ffeef0' : '#fffbeb',
                                    border: `1px solid ${validationResults.recommendation === 'FULL_APPROVAL' ? '#bbf7d0' : validationResults.recommendation === 'REJECT' ? '#ffc1c8' : '#fde68a'}`
                                }}>
                                    <div style={{ fontWeight: '700', fontSize: '14px', margin: 0 }}>{validationResults.recommendationText}</div>
                                    <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
                                        <strong>Eligible: </strong>{validationResults.eligibleUsers?.length > 0 ? validationResults.eligibleUsers.join(', ') : 'None'}
                                        {validationResults.ineligibleUsers?.length > 0 && (
                                            <span> · <strong style={{ color: '#d13212' }}>Not Eligible: </strong>{validationResults.ineligibleUsers.join(', ')}</span>
                                        )}
                                    </div>
                                </div>
                                {selectedUsers.length > 0 && (
                                    <div style={{ marginTop: '10px', padding: '8px 12px', backgroundColor: '#eef6ff', borderRadius: '6px', border: '1px solid #b8d4f0', fontSize: '13px' }}>
                                        📝 <strong>{selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''}</strong> selected for submission
                                    </div>
                                )}
                            </div>

                            {/* Reassurance banner — shown when users are flagged Not Found / Not Eligible */}
                            {(validationResults.totalNotFound > 0 || validationResults.totalIneligible > 0) && (
                                <div style={{ padding: '14px 16px', borderRadius: '10px', backgroundColor: '#eef6ff', border: '2px solid #4a90d9', borderLeft: '6px solid #0073bb', marginBottom: '14px', fontSize: '14px', color: '#12324d', lineHeight: 1.55, boxShadow: '0 1px 4px rgba(0,115,187,0.12)' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', fontSize: '15px', marginBottom: '8px', color: '#0073bb' }}>
                                        <span style={{ fontSize: '20px' }}>ℹ️</span> Please read
                                    </div>
                                    {validationResults.totalNotFound > 0 && (
                                        <div style={{ marginBottom: validationResults.totalIneligible > 0 ? '8px' : 0 }}>
                                            Seeing users marked <strong>"Not Found"</strong>? This can happen due to a temporary data sync delay in our portal — it does <strong>not</strong> always mean the user is invalid. You can still select those users and submit the request; our team will verify during review.
                                        </div>
                                    )}
                                    {validationResults.totalIneligible > 0 && (
                                        <div>
                                            Seeing users marked <strong>"Not Eligible"</strong>? A user may be flagged if one eligibility check isn't met in our current data. If you believe this is incorrect, you can still select the user and submit; you'll be asked to add a short justification and our team will review.
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Checks applied info */}
                            <div style={{ padding: '10px 14px', borderRadius: '8px', background: '#f8f9fa', border: '1px solid #e2e6ea', marginBottom: '12px', fontSize: '12px', color: '#555' }}>
                                <strong>Checks applied:</strong>{' '}
                                {isOnlyBasePI ? (isListed ? 'Department (listed use case)' : 'None (Base PI only)') : [
                                    needsBasePI && 'Base PI Access',
                                    needsActivity && 'Activity (≤30 days inactive)',
                                    needsJobLevel && 'Job Level (≥3)',
                                    needsSearchActivity && 'Search Activity (info only)'
                                ].filter(Boolean).join(' · ')}
                            </div>

                            {/* Department hard block (listed use case, every tier): the users the server refuses */}
                            {(() => {
                                const blocked = getBlockedUsers();
                                const list = blocked.length > 0 ? blocked
                                    : ((serverDeptBlock && serverDeptBlock.users) || []).map(u => ({ login: u.login, department: u.department, departmentMatch: u.match }));
                                if (list.length === 0) return null;
                                const unknown = list.filter(r => r.departmentMatch === 'UNKNOWN');
                                return (
                                    <div role="alert" style={{ padding: '14px 16px', borderRadius: '8px', background: '#ffeef0', border: '1px solid #ffc1c8', color: '#7f1d1d', marginBottom: '12px', fontSize: '13px' }}>
                                        <div style={{ marginBottom: '6px' }}>
                                            <strong>{list.length} user(s)</strong> are in departments that aren't part of the <strong>{teamLabel}</strong> approved use case, so they can't be added to this request.
                                            {' '}Users from other departments aren't eligible for this approved use case. To request access for them, use <strong>My team is not listed</strong> (unlisted use case), which goes to their leader and CTPS for review.
                                        </div>
                                        <ul style={{ margin: '4px 0 8px 18px', padding: 0 }}>
                                            {list.map(r => (
                                                <li key={r.login}>{r.login} ({r.departmentMatch === 'UNKNOWN' ? 'department not confirmed' : (r.department || 'unknown')})</li>
                                            ))}
                                        </ul>
                                        {unknown.length > 0 && (
                                            <div style={{ marginBottom: '8px' }}>
                                                We couldn't confirm the department of {unknown.length} user(s) from CARDS. Run validation again. If it still fails, contact CTPS (CTPS-Permissions-Security) or use the unlisted path.
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <button type="button" onClick={() => removeBlockedUsers(list)}
                                                style={{ padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: '1px solid #d13212', background: '#fff', color: '#d13212' }}>
                                                Remove these users
                                            </button>
                                            <button type="button" onClick={() => startUnlistedForBlocked(list)}
                                                style={{ padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', border: 'none', background: '#0073bb', color: '#fff' }}>
                                                Start an unlisted request for these users
                                            </button>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Results table — only relevant columns (Base-PI-only shows it when a row is flagged) */}
                            {(!isOnlyBasePI || basePIOnlyFlagged) && validationResults.tableData && validationResults.tableData.length > 0 && (
                                <div>
                                    {/* Filter bar */}
                                    <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '12px', color: '#555', fontWeight: '600', marginRight: '4px' }}>Filter:</span>
                                        {['all', 'eligible', 'not_eligible'].map(f => {
                                            const count = f === 'all' ? validationResults.tableData.length
                                                : f === 'eligible' ? validationResults.tableData.filter(r => r.eligibleBool).length
                                                : validationResults.tableData.filter(r => !r.eligibleBool).length;
                                            const labels = { all: `All (${count})`, eligible: `✅ Eligible (${count})`, not_eligible: `❌ Not Eligible (${count})` };
                                            return (
                                                <button key={f} type="button" onClick={() => setTableFilter(f)}
                                                    style={{ padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                                                        border: `1px solid ${tableFilter === f ? '#0073bb' : '#ddd'}`,
                                                        background: tableFilter === f ? '#f0f8ff' : '#fff',
                                                        color: tableFilter === f ? '#0073bb' : '#666' }}>
                                                    {labels[f]}
                                                </button>
                                            );
                                        })}
                                    </div>
                                <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e6ea' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e2e6ea' }}>
                                                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>
                                                    <input type="checkbox" aria-label="Select all users that can be submitted"
                                                        checked={selectedUsers.length > 0 && selectedUsers.length === validationResults.tableData.filter(r => !rowBlocked(r, isListed)).length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedUsers(validationResults.tableData.filter(r => !rowBlocked(r, isListed)).map(r => r.login));
                                                            } else {
                                                                setSelectedUsers([]);
                                                            }
                                                        }}
                                                        style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                                                    />
                                                </th>
                                                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Login</th>
                                                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Department</th>
                                                {needsActivity && <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Activity</th>}
                                                {needsBasePI && <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Base PI</th>}
                                                {needsJobLevel && <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Job Level</th>}
                                                {needsSearchActivity && <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Search Activity</th>}
                                                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', color: '#555' }}>Eligible</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {validationResults.tableData
                                                .filter(row => {
                                                    if (tableFilter === 'eligible') return row.eligibleBool;
                                                    if (tableFilter === 'not_eligible') return !row.eligibleBool;
                                                    return true;
                                                })
                                                .map((row, idx) => {
                                                const isUserSelected = selectedUsers.includes(row.login);
                                                const isBlocked = rowBlocked(row, isListed);
                                                const rowBg = row.eligibleBool ? '#f0fdf4' : (row.eligible.includes('Not Found') ? '#fffbeb' : '#ffeef0');
                                                return (
                                                    <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0', backgroundColor: rowBg, opacity: isUserSelected ? 1 : 0.55 }}>
                                                        <td style={{ padding: '10px 12px' }}>
                                                            <input type="checkbox" checked={isUserSelected && !isBlocked} disabled={isBlocked}
                                                                aria-label={isBlocked ? `${row.login}: ${deptReason(row, rowTiers(row)[0], isListed, teamLabel) || 'not eligible for this approved use case'}` : `Select ${row.login}`}
                                                                onChange={() => {
                                                                    if (isBlocked) return;
                                                                    if (isUserSelected) {
                                                                        setSelectedUsers(selectedUsers.filter(u => u !== row.login));
                                                                    } else {
                                                                        setSelectedUsers([...selectedUsers, row.login]);
                                                                    }
                                                                }}
                                                                style={{ width: '15px', height: '15px', cursor: isBlocked ? 'not-allowed' : 'pointer' }}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '10px 12px', fontWeight: '600' }}>{row.login}</td>
                                                        <td style={{ padding: '10px 12px', fontSize: '12px' }}>
                                                            {row.department || '—'}{row.departmentSource === 'csv' ? ' (CSV)' : ''}
                                                            {isListed && (() => {
                                                                // Server per-user department result (exact, case-insensitive, on the team's list);
                                                                // a value computed here for old rows is information only.
                                                                const server = row.departmentMatchSource === 'server';
                                                                if (row.departmentMatch === 'MISMATCH') {
                                                                    return <span style={{ display: 'block', fontSize: '10px', color: server ? '#d13212' : '#d4880f', fontWeight: '600', marginTop: '2px' }}>
                                                                        {server ? `🚫 Not eligible for this approved use case: department ${row.department} isn't on ${teamLabel}'s list` : "⚠️ May not be eligible: department isn't on the team's list"}
                                                                    </span>;
                                                                }
                                                                if (row.departmentMatch === 'UNKNOWN') {
                                                                    return <span style={{ display: 'block', fontSize: '10px', color: server ? '#d13212' : '#d4880f', fontWeight: '600', marginTop: '2px' }}>
                                                                        {server ? "❓ Department couldn't be confirmed from CARDS" : '⚠️ Department unknown'}
                                                                    </span>;
                                                                }
                                                                return null;
                                                            })()}
                                                        </td>
                                                        {needsActivity && (
                                                            <td style={{ padding: '10px 12px', color: row.inactivity === 'Active' ? '#1a7f37' : row.inactivity === 'Inactive' ? '#d13212' : '#666', fontWeight: '600', fontSize: '12px' }}>
                                                                {row.inactivity === 'Active' ? '✅ Active' : row.inactivity === 'Inactive' ? '❌ Inactive' : row.inactivity}
                                                            </td>
                                                        )}
                                                        {needsBasePI && (
                                                            <td style={{ padding: '10px 12px', color: row.basePIAccess === 'Yes' ? '#1a7f37' : row.basePIAccess === 'No' ? '#d13212' : '#666', fontWeight: '600', fontSize: '12px' }}>
                                                                {row.basePIAccess === 'Yes' ? '✅ Yes' : row.basePIAccess === 'No' ? '❌ No' : row.basePIAccess}
                                                            </td>
                                                        )}
                                                        {needsJobLevel && (
                                                            <td style={{ padding: '10px 12px', fontWeight: '600', fontSize: '12px', color: parseInt(row.jobLevel) >= 3 ? '#1a7f37' : parseInt(row.jobLevel) < 3 ? '#d13212' : '#666' }}>
                                                                {row.jobLevel !== 'N/A' ? `L${row.jobLevel}` : 'N/A'}
                                                            </td>
                                                        )}
                                                        {needsSearchActivity && (
                                                            <td style={{ padding: '10px 12px' }}>
                                                                {row.searchActivity === 'PASS' && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534' }}>✅ Sufficient</span>}
                                                                {row.searchActivity === 'BELOW_THRESHOLD' && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#92400e' }}>⚠️ Low</span>}
                                                                {(!row.searchActivity || row.searchActivity === 'NO_DATA') && <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3f4f6', color: '#6b7280' }}>— No Data</span>}
                                                            </td>
                                                        )}
                                                        <td style={{ padding: '10px 12px' }}>
                                                            {row.eligibleBool 
                                                                ? <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>✅ Eligible</span>
                                                                : row.eligible.includes('Not Found')
                                                                    ? <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>⚠️ Not Found</span>
                                                                    : <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', backgroundColor: '#ffeef0', color: '#d13212', border: '1px solid #ffc1c8' }}>❌ Not Eligible</span>
                                                            }
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                </div>
                            )}

                            {/* Base PI only: simple confirmation (when no row is flagged) */}
                            {isOnlyBasePI && !basePIOnlyFlagged && (
                                <div style={{ padding: '12px 16px', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '13px', color: '#166534' }}>
                                    ✅ Base PI requires no validation checks. All {validationResults.totalUsers} user(s) are eligible.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Error */}
                    {submitError && (
                        <div style={{ padding: '12px', borderRadius: '6px', background: '#ffeef0', border: '1px solid #ffc1c8', color: '#d13212', marginBottom: '16px', fontSize: '13px' }}>
                            ❌ {submitError}
                        </div>
                    )}

                    {/* Exception Details — per risk type. Inactivity = per-user note;
                        Base PI & Job Level = ONE shared note each (process-level). */}
                    {validationResults && (() => {
                        if (riskTypesWithExceptions.length === 0) return null;

                        const sharedTemplate = 'As a [job family(s)/job title] team working in [org] [function/process], these associates need access to [permission] to look up/take action on [what information]. We use this to [X]. Without this access [Y - quantify impact].';

                        return (
                            <div style={{ marginBottom: '16px' }}>
                                {riskTypesWithExceptions.map(rt => {
                                    const inactiveUsers = getInactiveUsersForRiskType(rt);
                                    const noBasePIUsers = getNoBasePIUsersForRiskType(rt);
                                    const lowJobLevelUsers = getLowJobLevelUsersForRiskType(rt);
                                    if (inactiveUsers.length === 0 && noBasePIUsers.length === 0 && lowJobLevelUsers.length === 0) return null;
                                    const totalIneligible = getIneligibleForRiskType(rt).length;
                                    const rtPerms = riskGroups[rt]?.map(p => p.name).join(', ') || rt;

                                    const basePIKey = `${rt}:basePI`;
                                    const jobLevelKey = `${rt}:jobLevel`;

                                    return (
                                        <div key={rt} style={{ marginBottom: '16px', borderLeft: '4px solid #d4880f', paddingLeft: '16px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                                                <span style={getRiskBadgeStyle(rt)}>{rt}</span>
                                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#92400e' }}>
                                                    Exception Details ({totalIneligible} user{totalIneligible !== 1 ? 's' : ''})
                                                </span>
                                            </div>
                                            <div style={{ padding: '10px 14px', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a', marginBottom: '12px', fontSize: '13px', color: '#92400e' }}>
                                                Ineligible users for <strong>{rtPerms}</strong>. Inactivity is explained per user; Base PI and Job Level are explained once for the whole group.
                                            </div>

                                            {/* ── Inactivity: PER-USER ── */}
                                            {inactiveUsers.map((user, idx) => {
                                                const noteKey = `${rt}:${user.login}`;
                                                const noteValue = ineligibleNotes[noteKey] || '';
                                                const isComplete = noteValue.trim().length > 0;
                                                return (
                                                    <div key={user.login} style={{
                                                        marginBottom: '10px', padding: '12px 14px', borderRadius: '8px',
                                                        backgroundColor: isComplete ? '#f0fdf4' : '#fff',
                                                        border: `1px solid ${isComplete ? '#bbf7d0' : '#e2e6ea'}`,
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#333' }}>
                                                                {idx + 1}. {user.login}
                                                                {isComplete && <span style={{ color: '#1a7f37', marginLeft: '6px' }}>✅</span>}
                                                            </span>
                                                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#ffeef0', color: '#d13212', border: '1px solid #ffc1c8' }}>
                                                                Inactive ({user.inactiveDays} days)
                                                            </span>
                                                        </div>
                                                        <textarea
                                                            value={noteValue}
                                                            onChange={(e) => setIneligibleNotes(prev => ({ ...prev, [noteKey]: e.target.value }))}
                                                            placeholder="Why does this specific user need access despite being inactive?"
                                                            rows={3}
                                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }}
                                                        />
                                                    </div>
                                                );
                                            })}

                                            {/* ── Base PI: ONE SHARED note ── */}
                                            {noBasePIUsers.length > 0 && (() => {
                                                const val = sharedExceptions[basePIKey] || '';
                                                const isComplete = val.trim().length > 0;
                                                return (
                                                    <div style={{ marginBottom: '10px', padding: '12px 14px', borderRadius: '8px', backgroundColor: isComplete ? '#f0fdf4' : '#fff', border: `1px solid ${isComplete ? '#bbf7d0' : '#e2e6ea'}` }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#333' }}>
                                                                Exception {isComplete && <span style={{ color: '#1a7f37' }}>✅</span>}
                                                            </span>
                                                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#ffeef0', color: '#d13212', border: '1px solid #ffc1c8' }}>
                                                                {noBasePIUsers.length} user{noBasePIUsers.length !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px' }}>
                                                            Applies to: {noBasePIUsers.map(u => u.login).join(', ')}
                                                        </div>
                                                        <textarea
                                                            value={val}
                                                            onChange={(e) => setSharedExceptions(prev => ({ ...prev, [basePIKey]: e.target.value }))}
                                                            placeholder="One shared explanation for why this group needs access without Base PI..."
                                                            rows={3}
                                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }}
                                                        />
                                                        {!val && (
                                                            <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', fontSize: '11px', color: '#888', lineHeight: '1.7', fontStyle: 'italic' }}>
                                                                <strong style={{ color: '#666', fontStyle: 'normal' }}>Template:</strong> {sharedTemplate}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {/* ── Job Level: ONE SHARED note ── */}
                                            {lowJobLevelUsers.length > 0 && (() => {
                                                const val = sharedExceptions[jobLevelKey] || '';
                                                const isComplete = val.trim().length > 0;
                                                return (
                                                    <div style={{ marginBottom: '10px', padding: '12px 14px', borderRadius: '8px', backgroundColor: isComplete ? '#f0fdf4' : '#fff', border: `1px solid ${isComplete ? '#bbf7d0' : '#e2e6ea'}` }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                                                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#333' }}>
                                                                Job Level — shared exception {isComplete && <span style={{ color: '#1a7f37' }}>✅</span>}
                                                            </span>
                                                            <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#ffeef0', color: '#d13212', border: '1px solid #ffc1c8' }}>
                                                                {lowJobLevelUsers.length} user{lowJobLevelUsers.length !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px' }}>
                                                            Applies to: {lowJobLevelUsers.map(u => u.login).join(', ')}
                                                        </div>
                                                        <textarea
                                                            value={val}
                                                            onChange={(e) => setSharedExceptions(prev => ({ ...prev, [jobLevelKey]: e.target.value }))}
                                                            placeholder="One shared explanation for why this group needs access despite job level..."
                                                            rows={3}
                                                            style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '13px', resize: 'vertical' }}
                                                        />
                                                        {!val && (
                                                            <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '6px', background: '#f8f9fa', border: '1px solid #e9ecef', fontSize: '11px', color: '#888', lineHeight: '1.7', fontStyle: 'italic' }}>
                                                                <strong style={{ color: '#666', fontStyle: 'normal' }}>Template:</strong> {sharedTemplate}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })}

                                {/* Overall status */}
                                {(() => {
                                    const allFilled = riskTypesWithExceptions.every(rt =>
                                        getInactiveUsersForRiskType(rt).every(u => (ineligibleNotes[`${rt}:${u.login}`] || '').trim().length > 0)
                                        && (getNoBasePIUsersForRiskType(rt).length === 0 || (sharedExceptions[`${rt}:basePI`] || '').trim().length > 0)
                                        && (getLowJobLevelUsersForRiskType(rt).length === 0 || (sharedExceptions[`${rt}:jobLevel`] || '').trim().length > 0)
                                    );
                                    return (
                                        <div style={{
                                            padding: '8px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '600',
                                            backgroundColor: allFilled ? '#dcfce7' : '#ffeef0',
                                            color: allFilled ? '#166534' : '#d13212',
                                            border: `1px solid ${allFilled ? '#bbf7d0' : '#ffc1c8'}`,
                                        }}>
                                            {allFilled
                                                ? '✅ All exception details provided — ready to submit'
                                                : '❌ Exception details still needed'}
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })()}

                    {/* NEMO Review Gate — only for Unlisted CRP/UFS with non-ops users */}
                    {validationResults && selectedUsers.length > 0 && useCaseType === 'unlisted' && hasCRP(selectedArray) && (() => {
                        const nonOpsCount = validationResults.nonOpsCount || 0;
                        if (nonOpsCount === 0) return null;
                        return (
                            <div style={{ marginBottom: '16px', borderRadius: '10px', border: '2px solid #d4880f', overflow: 'hidden' }}>
                                <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg, #92400e, #b45309)', color: '#fff' }}>
                                    <div style={{ fontWeight: '700', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span>🔔</span> NEMO Review Required
                                    </div>
                                    <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.9 }}>
                                        {nonOpsCount} non-operations user(s) detected — review required before submission
                                    </div>
                                </div>
                                <div style={{ padding: '16px', background: '#fff' }}>
                                    <p style={{ fontSize: '13px', color: '#333', margin: '0 0 12px 0', lineHeight: '1.5' }}>
                                        NEMO provides similar lookup capabilities and may be a better fit for non-operations roles. Please review the NEMO documentation to determine if it meets your team's needs before proceeding with UFS.
                                    </p>
                                    <a href="https://w.amazon.com/bin/view/SecurityWorkpaceAccessTeam/NEMO/" target="_blank" rel="noopener noreferrer"
                                        style={{ display: 'inline-block', padding: '8px 16px', borderRadius: '6px', background: '#92400e', color: '#fff', fontSize: '13px', fontWeight: '600', textDecoration: 'none', marginBottom: '16px' }}>
                                        📖 Review NEMO Wiki Documentation →
                                    </a>
                                    <div style={{ padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e6ea', background: '#f8f9fa' }}>
                                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px', color: '#333' }}>
                                            <input type="checkbox" checked={nemoConfirmed} onChange={(e) => setNemoConfirmed(e.target.checked)}
                                                style={{ width: '18px', height: '18px', marginTop: '2px' }} />
                                            <span>I have reviewed the NEMO documentation and confirm that NEMO <strong>does not meet</strong> our team's requirements. We still require UFS access for the use case described above.</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Submit — only after validation + user selection + exception notes + use case + NEMO */}
                    {validationResults && selectedUsers.length > 0 && (() => {
                        const ineligibleNotesOk = riskTypesWithExceptions.length === 0 ||
                            riskTypesWithExceptions.every(rt =>
                                getInactiveUsersForRiskType(rt).every(u => (ineligibleNotes[`${rt}:${u.login}`] || '').trim().length > 0)
                                && (getNoBasePIUsersForRiskType(rt).length === 0 || (sharedExceptions[`${rt}:basePI`] || '').trim().length > 0)
                                && (getLowJobLevelUsersForRiskType(rt).length === 0 || (sharedExceptions[`${rt}:jobLevel`] || '').trim().length > 0)
                            );
                        // Use cases already validated in Step 5 (can't reach Step 6 without them)
                        const nemoOk = useCaseType === 'unlisted'
                            ? (!hasCRP(selectedArray) || (validationResults.nonOpsCount || 0) === 0 || nemoConfirmed)
                            : true;
                        const canSubmit = ineligibleNotesOk && nemoOk;
                        
                        return (
                            <button type="button" onClick={handleSubmit} disabled={isSubmitting || !canSubmit}
                                style={{
                                    width: '100%', padding: '14px', borderRadius: '8px', fontSize: '15px',
                                    fontWeight: '700', cursor: (isSubmitting || !canSubmit) ? 'not-allowed' : 'pointer', border: 'none',
                                    background: !canSubmit ? '#e2e6ea' : '#ff9900', 
                                    color: !canSubmit ? '#888' : '#000', marginBottom: '20px',
                                    opacity: !canSubmit ? 0.7 : 1
                                }}>
                                {isSubmitting ? '⏳ Submitting...' : `📤 Submit Request (${selectedPermissions.size} permissions, ${selectedUsers.length} users)`}
                            </button>
                        );
                    })()}
            </PageLayout>
        );
    }

    return null;
};

export default PermissionRequest;
